import { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";

/* ═══════════════════════════════════════════
   ONYX CERAMIC CAR CARE — Premium Detailing
   Dark + Light Theme Support
   ═══════════════════════════════════════════ */

// ─── THEME SYSTEM ───
const THEMES = {
  dark: {
    name: "dark",
    bg0: "#0b0b0b",
    bg1: "#111111",
    bg2: "#161616",
    bg3: "#1c1c1c",
    red: "#E63946",
    redDark: "#c0392b",
    text: "#ffffff",
    textSoft: "#e0e0e0",
    textMuted: "#cccccc",
    textFaded: "#888888",
    textDim: "#555555",
    card: "#161616",
    cardBorder: "rgba(255,255,255,0.06)",
    cardBorderHover: "rgba(230,57,70,0.25)",
    inputBg: "rgba(255,255,255,0.04)",
    inputBorder: "rgba(255,255,255,0.2)",
    inputText: "#ffffff",
    inputPlaceholder: "#777777",
    overlayBg: "rgba(0,0,0,0.75)",
    headerBg: "rgba(11,11,11,0.96)",
    headerBorder: "rgba(230,57,70,0.12)",
    shadowCard: "0 20px 50px rgba(0,0,0,0.35)",
    navLink: "#cccccc",
    footerText: "#bbbbbb",
    divider: "rgba(255,255,255,0.04)",
    selectBg: "#111111",
    mapFilter: "grayscale(1) invert(0.92) contrast(1.2)",
    logoFilter: "none",
    scrollThumb: "rgba(230,57,70,0.3)",
  },
  light: {
    name: "light",
    bg0: "#f5f3f0",
    bg1: "#edeae6",
    bg2: "#ffffff",
    bg3: "#f9f7f5",
    red: "#D62839",
    redDark: "#b71c2a",
    text: "#1a1a1a",
    textSoft: "#2d2d2d",
    textMuted: "#4a4a4a",
    textFaded: "#777777",
    textDim: "#999999",
    card: "#ffffff",
    cardBorder: "rgba(0,0,0,0.08)",
    cardBorderHover: "rgba(214,40,57,0.3)",
    inputBg: "#ffffff",
    inputBorder: "rgba(0,0,0,0.12)",
    inputText: "#1a1a1a",
    inputPlaceholder: "#999999",
    overlayBg: "rgba(0,0,0,0.5)",
    headerBg: "rgba(245,243,240,0.96)",
    headerBorder: "rgba(0,0,0,0.06)",
    shadowCard: "0 10px 40px rgba(0,0,0,0.08)",
    navLink: "#4a4a4a",
    footerText: "#555555",
    divider: "rgba(0,0,0,0.06)",
    selectBg: "#ffffff",
    mapFilter: "none",
    logoFilter: "brightness(0.2)",
    scrollThumb: "rgba(214,40,57,0.35)",
  }
};

const ThemeContext = createContext(THEMES.dark);
const useTheme = () => useContext(ThemeContext);

// Legacy constants (kept for non-themed elements)
const RED = "#E63946";
const RED_DARK = "#c0392b";

const SERVICES_LIST = [
  { id: "ppf", title: "PPF - Paint Protection Film", short: "Defend Against Wear & Tear.", icon: "shield",
    desc: "Self-healing film that shields your paint from scratches, rock chips, and UV damage.",
    img: "./assets/images/ppf-service.jpg" },
  { id: "ceramic-coating", title: "Ceramic Coating", short: "Protect your car the professional way.", icon: "droplet",
    desc: "9H/10H nano-ceramic coating for extreme gloss, hydrophobic protection, and lasting shine.",
    img: "./assets/images/ceramic-service.jpg" },
  { id: "car-wash", title: "Premium Car Wash", short: "Unveil the Brilliance of your vehicle.", icon: "car",
    desc: "pH-balanced foam wash with two-bucket method, iron decontamination, and spot-free rinse.",
    img: "https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?w=600&q=80" },
  { id: "interior", title: "Interior Detailing", short: "Revitalize Your Ride, Inside and Out.", icon: "sofa",
    desc: "Deep steam cleaning, leather conditioning, dashboard restoration, and odor elimination.",
    img: "./assets/images/interior-service.jpg" },
  { id: "paint-correction", title: "Paint Correction", short: "Restore the original factory finish.", icon: "sparkle",
    desc: "Multi-stage machine polishing to remove swirl marks, scratches, and oxidation.",
    img: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=600&q=80" },
  { id: "graphene", title: "Graphene Coating", short: "Next-Generation surface protection.", icon: "layers",
    desc: "Superior heat dissipation, reduced water spotting, and enhanced scratch resistance.",
    img: "https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=600&q=80" },
  { id: "sun-film", title: "Sun Film", short: "Beat the heat, protect your ride.", icon: "sunfilm",
    desc: "Premium window tinting with UV protection, heat rejection, and enhanced privacy.",
    img: "https://images.unsplash.com/photo-1489824904134-891ab64532f1?w=600&q=80" },
  { id: "4x4-accessories", title: "4x4 Accessories", short: "Gear up for every adventure.", icon: "offroad",
    desc: "Bull bars, roof racks, LED lights, and performance upgrades for off-road vehicles.",
    img: "https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=600&q=80" },
];

const BRANDS = [
  { name: "BMW", logo: "./assets/brands/bmw.svg" },
  { name: "Audi", logo: "./assets/brands/audi.svg" },
  { name: "Mercedes", logo: "./assets/brands/mercedes.svg" },
  { name: "Ferrari", logo: "./assets/brands/ferrari.svg" },
  { name: "Volvo", logo: "./assets/brands/volvo.svg" },
  { name: "Land Rover", logo: "./assets/brands/landrover.svg" },
  { name: "Lexus", logo: "./assets/brands/lexus.svg" },
  { name: "Rolls Royce", logo: "./assets/brands/rollsroyce.svg" },
  { name: "Tesla", logo: "./assets/brands/tesla.svg" },
  { name: "Toyota", logo: "./assets/brands/toyota.svg" },
  { name: "Honda", logo: "./assets/brands/honda.svg" },
  { name: "Hyundai", logo: "./assets/brands/hyundai.svg" },
  { name: "Kia", logo: "./assets/brands/kia.svg" },
  { name: "Jeep", logo: "./assets/brands/jeep.svg" },
  { name: "Volkswagen", logo: "./assets/brands/volkswagen.svg" },
  { name: "Skoda", logo: "./assets/brands/skoda.svg" },
  { name: "Ford", logo: "./assets/brands/ford.svg" },
  { name: "Nissan", logo: "./assets/brands/nissan.svg" },
  { name: "Mini", logo: "./assets/brands/mini.svg" },
  { name: "Suzuki", logo: "./assets/brands/suzuki.svg" },
  { name: "Chevrolet", logo: "./assets/brands/chevrolet.svg" },
  { name: "Fiat", logo: "./assets/brands/fiat.svg" },
  { name: "Isuzu", logo: "./assets/brands/isuzu.svg" },
  { name: "Mitsubishi", logo: "./assets/brands/mitsubishi.svg" },
  { name: "Seat", logo: "./assets/brands/seat.svg" },
];

const TESTIMONIALS = [
  { name: "Siri' Ishaa", car: "Google Review", rating: 5, text: "I recently got my car treated here and I'm impressed with the quality of work. The finish looks amazing and the team explained everything clearly. They take proper care of the vehicle and deliver clean, quality work. Satisfied with the service!" },
  { name: "Deepak Raj Dandu", car: "Local Guide · Google Review", rating: 5, text: "Incredible job by Onyx Ceramic Car Care on my brother's car! We brought it in for a full PPF installation and a wash, and the results are absolutely flawless. The PPF is completely seamless with a mirror-like finish, and the car looks better than brand new. Highly recommend their professional and meticulous work!" },
  { name: "Ashok Varma", car: "Google Review", rating: 5, text: "Got my car's PPF done here and I'm really happy with the results. The team is friendly and knows what they're doing. The finish looks clean, edges are well done, and the overall quality is solid. Good value for the work. Will definitely come back for future detailing needs." },
  { name: "Yalamarthi Venkata Kiran", car: "Local Guide · Google Review", rating: 5, text: "The owner is a car enthusiast and he takes and walks about cars... they have done a fantastic job on my vehicle with a high quality PPF. My advice to everyone — just walk in and talk to the owner for your requirement and you will be more happy with the results!" },
];

// ─── SVG ICONS ───
const Icons = {
  shield: (s = 36) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>,
  droplet: (s = 36) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>,
  car: (s = 36) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5" strokeLinecap="round"><path d="M5 17h14M5 17a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1l2-3h8l2 3h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2"/><circle cx="7.5" cy="17" r="2"/><circle cx="16.5" cy="17" r="2"/></svg>,
  sofa: (s = 36) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5" strokeLinecap="round"><path d="M4 11V8a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v3"/><path d="M2 11v5a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0H6a2 2 0 0 0-4 0z"/><path d="M4 18v2m16-2v2"/></svg>,
  sparkle: (s = 36) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5"><path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6L5.6 18.4"/></svg>,
  layers: (s = 36) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5" strokeLinecap="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>,
  sunfilm: (s = 36) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>,
  offroad: (s = 36) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5" strokeLinecap="round"><rect x="1" y="3" width="15" height="13" rx="2"/><polyline points="16 8 20 8 23 11 23 16 20 16"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/><line x1="8" y1="18.5" x2="16" y2="18.5"/></svg>,
  star: (s = 16) => <svg width={s} height={s} viewBox="0 0 24 24" fill={RED} stroke="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
  award: (s = 36) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="8" r="6"/><path d="M8.21 13.89L7 23l5-3 5 3-1.21-9.12"/></svg>,
  check: (s = 36) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  users: (s = 36) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  leaf: (s = 36) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5" strokeLinecap="round"><path d="M17 8C8 10 5.9 16.17 3.82 21.34l1.89.76.95-2.3c.48.17.98.3 1.34.3C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75"/></svg>,
  wind: (s = 36) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5" strokeLinecap="round"><path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/></svg>,
  phone: (s = 18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  mail: (s = 18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/></svg>,
  mapPin: (s = 18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  instagram: (s = 20) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>,
  youtube: (s = 20) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19.1c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/></svg>,
  facebook: (s = 20) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>,
  twitter: (s = 20) => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
  reddit: (s = 20) => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 0-.463.327.327 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>,
  linkedin: (s = 20) => <svg width={s} height={s} viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
  arrowRight: (s = 16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
  chevDown: (s = 12) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M6 9l6 6 6-6"/></svg>,
  menu: (s = 24) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
  x: (s = 24) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  whatsapp: (s = 24) => <svg width={s} height={s} viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
};

// ─── SCROLL REVEAL (Enhanced with directions) ───
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}

const getTransform = (dir, vis) => {
  if (vis) return "translate(0,0) scale(1)";
  switch(dir) {
    case "left": return "translateX(-60px)";
    case "right": return "translateX(60px)";
    case "up": return "translateY(40px)";
    case "down": return "translateY(-40px)";
    case "zoom": return "scale(0.85)";
    case "zoom-up": return "translateY(30px) scale(0.95)";
    case "flip": return "perspective(600px) rotateY(15deg)";
    default: return "translateY(40px)";
  }
};

const Reveal = ({ children, delay = 0, direction = "up", className = "", style = {} }) => {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} className={className} style={{
      opacity: vis ? 1 : 0,
      transform: getTransform(direction, vis),
      transition: `opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
      willChange: "opacity, transform",
      ...style
    }}>{children}</div>
  );
};

// ─── ANIMATED COUNTER ───
function AnimatedCounter({ end, suffix = "", duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [ref, vis] = useReveal(0.3);
  const started = useRef(false);
  
  useEffect(() => {
    if (!vis || started.current) return;
    started.current = true;
    const num = parseInt(end);
    const step = Math.max(1, Math.floor(num / (duration / 16)));
    let current = 0;
    const timer = setInterval(() => {
      current += step;
      if (current >= num) { current = num; clearInterval(timer); }
      setCount(current);
    }, 16);
    return () => clearInterval(timer);
  }, [vis, end, duration]);
  
  return <span ref={ref}>{count}{suffix}</span>;
}

// ─── FLOATING ANIMATION WRAPPER ───
const Float = ({ children, offset = 10, duration = 3 }) => (
  <div style={{
    animation: `float ${duration}s ease-in-out infinite`,
  }}>{children}</div>
);

// ─── STAGGER CONTAINER ───
const Stagger = ({ children, baseDelay = 0, increment = 0.1 }) => (
  <>
    {Array.isArray(children) ? children.map((child, i) => (
      <div key={i} style={{
        animationDelay: `${baseDelay + i * increment}s`
      }}>{child}</div>
    )) : children}
  </>
);

// ─── FIREBASE CONFIG (Syncs with Onyx CRM) ───
const FIREBASE_CONFIG = {
  apiKey: "AIzaSyCz938i404ry9pjyC3Sky5eEpMMjXfbDTI",
  authDomain: "onyxceramic-crm.firebaseapp.com",
  projectId: "onyxceramic-crm",
  storageBucket: "onyxceramic-crm.firebasestorage.app",
  messagingSenderId: "389842732879",
  appId: "1:389842732879:web:ed1c01b0645842a09ca73a"
};

async function getFirebase() {
  if (window.__fbInstance) return window.__fbInstance;
  const { initializeApp } = await import("https://www.gstatic.com/firebasejs/10.7.1/firebase-app.js");
  const { getFirestore, collection, addDoc, serverTimestamp, query, where, getDocs } = await import("https://www.gstatic.com/firebasejs/10.7.1/firebase-firestore.js");
  const app = initializeApp(FIREBASE_CONFIG);
  const db = getFirestore(app);
  window.__fbInstance = { db, collection, addDoc, serverTimestamp, query, where, getDocs };
  return window.__fbInstance;
}

async function submitBookingToFirebase(formData) {
  const fb = await getFirebase();
  const { db, collection, addDoc, serverTimestamp, query, where, getDocs } = fb;
  let customerId = null;
  try {
    const q = query(collection(db, "customers"), where("phone", "==", formData.phone));
    const snap = await getDocs(q);
    if (!snap.empty) customerId = snap.docs[0].id;
  } catch (e) { console.log("Customer lookup skipped:", e); }
  if (!customerId) {
    try {
      const ref = await addDoc(collection(db, "customers"), {
        name: formData.name, phone: formData.phone, email: formData.email || "",
        plate: "", vehicle: "", notes: "Website booking", createdAt: serverTimestamp(), source: "website"
      });
      customerId = ref.id;
    } catch (e) { console.error("Customer create failed:", e); }
  }
  const today = new Date();
  await addDoc(collection(db, "bookings"), {
    customerId: customerId || "", customerName: formData.name, customerPhone: formData.phone,
    service: formData.service, date: formData.date || today.toISOString().slice(0, 10),
    time: formData.time || "10:00", status: "scheduled", message: formData.message || "",
    source: "website", createdAt: serverTimestamp()
  });
  return true;
}

// ─── BOOKING POPUP (Firebase CRM Connected) ───
function BookingPopup({ open, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", date: "", time: "", message: "" });
  const [status, setStatus] = useState("idle");
  if (!open) return null;
  const handleSubmit = async () => {
    if (!form.name || !form.phone || !form.service) { alert("Please fill name, phone & service."); return; }
    setStatus("submitting");
    try { await submitBookingToFirebase(form); setStatus("done"); } catch (e) { console.error(e); setStatus("error"); }
  };
  const inp = { width: "100%", background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: 8, padding: "13px 16px", color: "var(--text)", fontFamily: "'Poppins', sans-serif", fontSize: "0.93rem", outline: "none", boxSizing: "border-box", transition: "border-color 0.3s" };
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--overlay)", backdropFilter: "blur(10px)", padding: 20 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--bg-1)", border: "1px solid rgba(230,57,70,0.2)", borderRadius: 16, padding: "40px 34px", maxWidth: 480, width: "100%", position: "relative", animation: "popIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)", maxHeight: "90vh", overflowY: "auto" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer" }}>{Icons.x(20)}</button>
        <div style={{ width: 48, height: 3, background: RED, borderRadius: 2, marginBottom: 20 }} />
        <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "1.3rem", fontWeight: 700, color: "var(--text)", marginBottom: 6 }}>Schedule Your Car Detailing</h3>
        <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.88rem", color: "var(--text-muted)", marginBottom: 24 }}>Book an appointment — syncs directly with our studio.</p>
        {status === "done" ? (
          <div style={{ textAlign: "center", padding: "30px 0" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(39,198,109,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#27c66d" strokeWidth="2" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg></div>
            <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: "1.25rem", color: "var(--text)", marginBottom: 6 }}>Booking Confirmed!</p>
            <p style={{ fontFamily: "'Poppins', sans-serif", color: "var(--text-muted)", fontSize: "0.9rem" }}>Your appointment has been scheduled. We'll call to confirm.</p>
          </div>
        ) : status === "error" ? (
          <div style={{ textAlign: "center", padding: "30px 0" }}>
            <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: "1.1rem", color: "var(--text)", marginBottom: 8 }}>Something went wrong</p>
            <p style={{ fontFamily: "'Poppins', sans-serif", color: "var(--text-muted)", fontSize: "0.88rem", marginBottom: 16 }}>Please try again or call +91 99564 56499</p>
            <button onClick={() => setStatus("idle")} style={{ background: "rgba(255,255,255,0.06)", color: "var(--text)", border: "1px solid var(--input-border)", padding: "10px 24px", borderRadius: 8, cursor: "pointer", fontFamily: "'Poppins', sans-serif" }}>Try Again</button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input placeholder="Your Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} style={inp} onFocus={e => e.target.style.borderColor = "rgba(230,57,70,0.4)"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.2)"} />
            <input placeholder="Phone Number *" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} style={inp} onFocus={e => e.target.style.borderColor = "rgba(230,57,70,0.4)"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.2)"} />
            <input placeholder="Email (optional)" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} style={inp} onFocus={e => e.target.style.borderColor = "rgba(230,57,70,0.4)"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.2)"} />
            <select value={form.service} onChange={e => setForm({...form, service: e.target.value})} style={{...inp, color: form.service ? "#fff" : "#777", appearance: "none"}}>
              <option value="" style={{ background: "var(--bg-1)" }}>— Select Service * —</option>
              {["9H Ceramic Coating (3yr)","10H Graphene Coating (5yr)","PPF Full Body","Interior Detailing","Paint Correction (2-Stage)","Engine Bay Detailing","Headlight Restoration","Windshield Coating"].map(s => <option key={s} value={s} style={{ background: "var(--bg-1)" }}>{s}</option>)}
            </select>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} min={new Date().toISOString().slice(0,10)} style={{...inp, color: form.date ? "#fff" : "#777"}} onFocus={e => e.target.style.borderColor = "rgba(230,57,70,0.4)"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.2)"} />
              <select value={form.time} onChange={e => setForm({...form, time: e.target.value})} style={{...inp, color: form.time ? "#fff" : "#777", appearance: "none"}}>
                <option value="" style={{ background: "var(--bg-1)" }}>Time Slot</option>
                {["09:00","10:00","11:00","12:00","14:00","15:00","16:00","17:00","18:00"].map(t => <option key={t} value={t} style={{ background: "var(--bg-1)" }}>{parseInt(t) > 12 ? `${parseInt(t)-12}:00 PM` : `${t} AM`}</option>)}
              </select>
            </div>
            <textarea placeholder="Vehicle details or requests (optional)" rows={2} value={form.message} onChange={e => setForm({...form, message: e.target.value})} style={{...inp, resize: "vertical"}} onFocus={e => e.target.style.borderColor = "rgba(230,57,70,0.4)"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.2)"} />
            <button onClick={handleSubmit} disabled={status === "submitting"} style={{
              background: status === "submitting" ? "#555" : `linear-gradient(135deg, ${RED}, ${RED_DARK})`,
              color: "var(--text)", border: "none", padding: "14px", borderRadius: 8, fontFamily: "'Poppins', sans-serif",
              fontSize: "0.85rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
              cursor: status === "submitting" ? "wait" : "pointer",
              boxShadow: status === "submitting" ? "none" : "0 4px 20px rgba(230,57,70,0.3)", marginTop: 4,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8
            }}>{status === "submitting" ? (<><svg width="18" height="18" viewBox="0 0 24 24" style={{ animation: "rotateSlow 1s linear infinite" }}><circle cx="12" cy="12" r="10" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="3"/><path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"/></svg>Booking...</>) : "Book Appointment"}</button>
            <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.7rem", color: "var(--text-dim)", textAlign: "center" }}>Syncs with our studio schedule. We'll confirm via call.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── HEADER ───
function Header({ navigate, page, onBook, theme, setTheme }) {
  const [scrolled, setScrolled] = useState(false);
  const [servOpen, setServOpen] = useState(false);
  const [mobOpen, setMobOpen] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const link = (label, pg) => (
    <button onClick={() => { navigate(pg); setMobOpen(false); }} style={{
      background: "none", border: "none", color: page === pg ? RED : "var(--nav-link)",
      fontFamily: "'Poppins', sans-serif", fontSize: "0.8rem", fontWeight: 400,
      letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer", padding: "8px 0",
      transition: "color 0.3s"
    }}
    onMouseEnter={e => e.target.style.color = RED}
    onMouseLeave={e => { if (page !== pg) e.target.style.color = "var(--text-muted)"; }}
    >{label}</button>
  );

  return (
    <header style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "var(--header-bg)" : "transparent",
      backdropFilter: scrolled ? "blur(20px)" : "none",
      borderBottom: scrolled ? "1px solid var(--header-border)" : "none",
      transition: "all 0.4s", padding: "0 clamp(16px, 4vw, 60px)"
    }}>
      {/* Top bar */}
      <div style={{
        maxWidth: 1400, margin: "0 auto", display: "flex", justifyContent: "space-between",
        alignItems: "center", padding: "7px 0",
        borderBottom: "1px solid var(--divider)", fontSize: "0.78rem",
        color: "var(--text-muted)", fontFamily: "'Poppins', sans-serif"
      }} className="top-bar">
        <span style={{ fontWeight: 600, letterSpacing: "0.1em" }}>Hyderabad's #1 Ceramic Car Care Studio</span>
        <div style={{ display: "flex", gap: 20 }}>
          <a href="tel:+919956456499" style={{ display: "flex", alignItems: "center", gap: 5, textDecoration: "none", color: "inherit", transition: "color 0.3s" }} onMouseEnter={e => e.currentTarget.style.color = RED} onMouseLeave={e => e.currentTarget.style.color = "inherit"}>{Icons.phone(11)} +91 99564 56499</a>
          <a href="mailto:info@onyxceramiccarcare.com" style={{ display: "flex", alignItems: "center", gap: 5, textDecoration: "none", color: "inherit", transition: "color 0.3s" }} onMouseEnter={e => e.currentTarget.style.color = RED} onMouseLeave={e => e.currentTarget.style.color = "inherit"}>{Icons.mail(11)} info@onyxceramiccarcare.com</a>
        </div>
      </div>

      <div style={{
        maxWidth: 1400, margin: "0 auto", display: "flex", alignItems: "center",
        justifyContent: "space-between", height: scrolled ? 58 : 64, transition: "height 0.4s"
      }}>
        <button onClick={() => navigate("home")} style={{
          background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center"
        }}>
          <img src={theme === "light" ? "./assets/logos/logo-light.svg" : "./assets/logos/onyxcermaic_logo.svg"} alt="Onyx Ceramic Car Care" style={{ height: scrolled ? 36 : 42, transition: "height 0.4s", objectFit: "contain" }} />
        </button>

        <nav style={{ display: "flex", alignItems: "center", gap: 26 }} className="desk-nav">
          {link("Home", "home")}
          <div style={{ position: "relative" }} onMouseEnter={() => setServOpen(true)} onMouseLeave={() => setServOpen(false)}>
            <button style={{
              background: "none", border: "none",
              color: ["ppf","ceramic-coating","car-wash","interior","paint-correction","graphene","sun-film","4x4-accessories"].includes(page) ? RED : "var(--nav-link)",
              fontFamily: "'Poppins', sans-serif", fontSize: "0.8rem", fontWeight: 400,
              letterSpacing: "0.14em", textTransform: "uppercase", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 5, padding: "8px 0"
            }}>Our Services {Icons.chevDown()}</button>
            <div style={{
              position: "absolute", top: "calc(100% + 4px)", left: -16, minWidth: 240,
              background: "var(--header-bg)", border: "1px solid rgba(230,57,70,0.15)",
              borderRadius: 10, padding: "8px 0", opacity: servOpen ? 1 : 0,
              pointerEvents: servOpen ? "auto" : "none",
              transform: servOpen ? "translateY(0)" : "translateY(-8px)",
              transition: "all 0.25s ease", backdropFilter: "blur(24px)"
            }}>
              {SERVICES_LIST.map(s => (
                <button key={s.id} onClick={() => { navigate(s.id); setServOpen(false); }} style={{
                  display: "block", width: "100%", textAlign: "left", padding: "11px 24px",
                  background: "none", border: "none", color: "var(--footer-text)",
                  fontFamily: "'Poppins', sans-serif", fontSize: "0.8rem", fontWeight: 400,
                  letterSpacing: "0.08em", cursor: "pointer", transition: "all 0.2s"
                }}
                onMouseEnter={e => { e.target.style.background = "rgba(230,57,70,0.08)"; e.target.style.color = RED; e.target.style.paddingLeft = "30px"; }}
                onMouseLeave={e => { e.target.style.background = "none"; e.target.style.color = "var(--footer-text)"; e.target.style.paddingLeft = "24px"; }}
                >{s.title}</button>
              ))}
            </div>
          </div>
          {link("Gallery", "gallery")}
          {link("About", "about")}
          {link("Blog", "blog")}
          {link("Contact", "contact")}
          <button onClick={onBook} style={{
            background: "linear-gradient(135deg, #E63946, #c0392b)", color: "#ffffff",
            border: "none", padding: "10px 24px", borderRadius: 6, fontFamily: "'Poppins', sans-serif",
            fontSize: "0.76rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase",
            cursor: "pointer", boxShadow: `0 4px 18px rgba(230,57,70,0.3)`, transition: "all 0.3s"
          }}
          onMouseEnter={e => e.target.style.transform = "translateY(-2px)"}
          onMouseLeave={e => e.target.style.transform = "translateY(0)"}
          >Book for Ultimate Shine</button>
          <ThemeToggle theme={theme} setTheme={setTheme} />
        </nav>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="mob-theme-toggle" style={{ display: "none" }}>
            <ThemeToggle theme={theme} setTheme={setTheme} />
          </div>
          <button className="mob-btn" onClick={() => setMobOpen(!mobOpen)} style={{
            background: "none", border: "none", color: "var(--text)", cursor: "pointer", display: "none"
          }}>{mobOpen ? Icons.x() : Icons.menu()}</button>
        </div>
      </div>

      {mobOpen && (
        <div className="mob-panel" style={{
          position: "fixed", top: 88, left: 0, right: 0, bottom: 0,
          background: "var(--header-bg)", backdropFilter: "blur(30px)",
          display: "flex", flexDirection: "column", padding: "28px 20px", gap: 2, overflowY: "auto"
        }}>
          {[["Home","home"],...SERVICES_LIST.map(s => [s.title, s.id]),["Gallery","gallery"],["About","about"],["Blog","blog"],["Contact","contact"]].map(([l,p]) => (
            <button key={p} onClick={() => { navigate(p); setMobOpen(false); }} style={{
              background: page === p ? "rgba(230,57,70,0.08)" : "none",
              border: "none", color: page === p ? RED : "var(--nav-link)",
              fontFamily: "'Poppins', sans-serif", fontSize: "0.85rem", fontWeight: 400,
              letterSpacing: "0.1em", textTransform: "uppercase", cursor: "pointer",
              padding: "13px 16px", textAlign: "left", borderRadius: 8,
              borderLeft: page === p ? `3px solid ${RED}` : "3px solid transparent"
            }}>{l}</button>
          ))}
          <button onClick={() => { onBook(); setMobOpen(false); }} style={{
            background: "linear-gradient(135deg, #E63946, #c0392b)", color: "#ffffff",
            border: "none", padding: "14px", borderRadius: 8, fontFamily: "'Poppins', sans-serif",
            fontSize: "0.85rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase",
            cursor: "pointer", marginTop: 14
          }}>Book Appointment</button>
        </div>
      )}
    </header>
  );
}

// ─── SECTION HEADING ───
const SectionTag = ({ tag, title, sub, align = "center" }) => (
  <div style={{ textAlign: align, marginBottom: 50 }}>
    {tag && <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.85rem", fontWeight: 400, letterSpacing: "0.25em", color: RED, textTransform: "uppercase", marginBottom: 10 }}>{tag}</p>}
    <h2 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "clamp(1.4rem, 3.5vw, 2.3rem)", fontWeight: 800, color: "var(--text)", lineHeight: 1.15, margin: "0 0 10px" }}>{title}</h2>
    {sub && <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "1rem", color: "var(--text-muted)", maxWidth: 560, margin: align === "center" ? "0 auto" : "0", lineHeight: 1.6 }}>{sub}</p>}
  </div>
);

// ─── HOME PAGE ───
function HomePage({ navigate, onBook }) {
  const [vidPhase, setVidPhase] = useState(0);
  const videoRef = useRef(null);

  // Sync text reveals to video timeline
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const handleTime = () => {
      const t = v.currentTime;
      if (t < 0.5) setVidPhase(0);
      else if (t < 2) setVidPhase(1);
      else if (t < 4) setVidPhase(2);
      else if (t < 6) setVidPhase(3);
      else setVidPhase(4);
    };
    v.addEventListener("timeupdate", handleTime);
    return () => v.removeEventListener("timeupdate", handleTime);
  }, []);

  return (
    <>
      {/* ═══ CINEMATIC HERO — Full-width video with centered overlay ═══ */}
      <section style={{
        minHeight: "100vh", position: "relative", overflow: "hidden",
        background: "#000", display: "flex", alignItems: "center", justifyContent: "center"
      }}>
        {/* Full-width Background Video */}
        <video
          ref={videoRef}
          autoPlay muted loop playsInline preload="auto"
          poster="./assets/videos/hero-poster.jpg"
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%", objectFit: "cover",
            zIndex: 0
          }}
        >
          <source src="./assets/videos/hero-video.mp4" type="video/mp4" />
        </video>

        {/* Cinematic dark overlay — vignette + bottom fade */}
        <div style={{
          position: "absolute", inset: 0, zIndex: 1,
          background: "radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%), linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 30%, transparent 60%, rgba(0,0,0,0.8) 100%)"
        }} />

        {/* Subtle red glow accent — top right */}
        <div style={{
          position: "absolute", top: "-10%", right: "-10%", width: "40vw", height: "40vw",
          background: "radial-gradient(circle, rgba(230,57,70,0.15) 0%, transparent 60%)",
          zIndex: 1, pointerEvents: "none"
        }} />

        {/* Bottom-left red glow */}
        <div style={{
          position: "absolute", bottom: "-5%", left: "-5%", width: "30vw", height: "30vw",
          background: "radial-gradient(circle, rgba(230,57,70,0.1) 0%, transparent 60%)",
          zIndex: 1, pointerEvents: "none"
        }} />

        {/* Centered Hero Content */}
        <div style={{
          position: "relative", zIndex: 2,
          maxWidth: 1100, width: "100%", textAlign: "center",
          padding: "120px clamp(20px, 5vw, 60px) 80px"
        }}>
          {/* Premium Badge — appears at start */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 10,
            padding: "10px 22px", borderRadius: 100,
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.15)",
            backdropFilter: "blur(20px)",
            marginBottom: 36,
            opacity: vidPhase >= 0 ? 1 : 0,
            transform: vidPhase >= 0 ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.8s ease, transform 0.8s ease"
          }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: RED, animation: "pulse 2s infinite" }} />
            <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.7rem", fontWeight: 500, color: "rgba(255,255,255,0.9)", letterSpacing: "0.25em", textTransform: "uppercase" }}>Hyderabad's Premier Detailing Studio</span>
          </div>

          {/* Main Headline — word by word reveal synced to video */}
          <h1 style={{
            fontFamily: "'Oswald', sans-serif",
            fontSize: "clamp(2.2rem, 7vw, 5.5rem)",
            fontWeight: 700, color: "#fff", lineHeight: 1.05,
            margin: "0 0 28px", letterSpacing: "-0.02em",
            textShadow: "0 4px 30px rgba(0,0,0,0.5)"
          }}>
            <span style={{
              display: "inline-block",
              opacity: vidPhase >= 1 ? 1 : 0,
              transform: vidPhase >= 1 ? "translateY(0) scale(1)" : "translateY(40px) scale(0.95)",
              filter: vidPhase >= 1 ? "blur(0)" : "blur(8px)",
              transition: "opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1), filter 1s ease"
            }}>UNLEASH THE</span>{" "}
            <span style={{
              display: "inline-block",
              opacity: vidPhase >= 2 ? 1 : 0,
              transform: vidPhase >= 2 ? "translateY(0) scale(1)" : "translateY(40px) scale(0.95)",
              filter: vidPhase >= 2 ? "blur(0)" : "blur(8px)",
              transition: "opacity 1s cubic-bezier(0.16, 1, 0.3, 1) 0.2s, transform 1s cubic-bezier(0.16, 1, 0.3, 1) 0.2s, filter 1s ease 0.2s"
            }}>ULTIMATE</span>
            <br/>
            <span style={{
              display: "inline-block",
              background: "linear-gradient(135deg, #E63946 0%, #ff6b6b 50%, #E63946 100%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              opacity: vidPhase >= 3 ? 1 : 0,
              transform: vidPhase >= 3 ? "translateY(0) scale(1)" : "translateY(40px) scale(0.95)",
              filter: vidPhase >= 3 ? "blur(0)" : "blur(8px)",
              transition: "opacity 1.2s cubic-bezier(0.16, 1, 0.3, 1) 0.3s, transform 1.2s cubic-bezier(0.16, 1, 0.3, 1) 0.3s, filter 1s ease 0.3s"
            }}>SHINE & PROTECTION</span>
          </h1>

          {/* Subtitle */}
          <p style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "clamp(0.95rem, 1.5vw, 1.15rem)",
            color: "rgba(255,255,255,0.8)", lineHeight: 1.7,
            maxWidth: 640, margin: "0 auto 48px",
            opacity: vidPhase >= 3 ? 1 : 0,
            transform: vidPhase >= 3 ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 1s ease 0.6s, transform 1s ease 0.6s",
            textShadow: "0 2px 10px rgba(0,0,0,0.5)"
          }}>
            Advanced Ceramic Coating, Self-Healing PPF & Luxury Car Care.<br/>
            Hyderabad's most trusted detailing studio in Balanagar.
          </p>

          {/* CTA Buttons */}
          <div style={{
            display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap",
            opacity: vidPhase >= 4 ? 1 : 0,
            transform: vidPhase >= 4 ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 1s ease, transform 1s ease"
          }}>
            <button onClick={onBook} style={{
              background: "linear-gradient(135deg, #E63946, #c0392b)", color: "#fff",
              border: "none", padding: "17px 42px", borderRadius: 8,
              fontFamily: "'Poppins', sans-serif", fontSize: "0.88rem", fontWeight: 700,
              letterSpacing: "0.15em", textTransform: "uppercase",
              cursor: "pointer", transition: "all 0.3s",
              boxShadow: "0 10px 40px rgba(230,57,70,0.4)"
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 15px 50px rgba(230,57,70,0.5)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 10px 40px rgba(230,57,70,0.4)"; }}
            >Book Now</button>
            <button onClick={() => { const el = document.getElementById('svc'); el && el.scrollIntoView({behavior:'smooth'}); }} style={{
              background: "rgba(255,255,255,0.05)", color: "#fff",
              border: "1px solid rgba(255,255,255,0.25)",
              padding: "17px 42px", borderRadius: 8,
              fontFamily: "'Poppins', sans-serif", fontSize: "0.88rem", fontWeight: 700,
              letterSpacing: "0.15em", textTransform: "uppercase",
              cursor: "pointer", transition: "all 0.3s",
              backdropFilter: "blur(20px)"
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#fff"; e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)"; e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
            >Explore Services</button>
          </div>

          {/* Stats Bar — fades in last */}
          <div style={{
            display: "flex", gap: "clamp(28px, 5vw, 60px)", justifyContent: "center", flexWrap: "wrap",
            marginTop: 80,
            opacity: vidPhase >= 4 ? 1 : 0,
            transform: vidPhase >= 4 ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 1s ease 0.3s, transform 1s ease 0.3s"
          }}>
            {[["500","+","Cars Detailed"],["50","+","Brands Served"],["100","%","Satisfaction"]].map(([n,s,l]) => (
              <div key={n} style={{ textAlign: "center" }}>
                <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: "clamp(1.6rem, 3vw, 2.2rem)", fontWeight: 700, color: "#fff", marginBottom: 4 }}>
                  <AnimatedCounter end={n} suffix={s} />
                </p>
                <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.72rem", color: "rgba(255,255,255,0.6)", letterSpacing: "0.15em", textTransform: "uppercase" }}>{l}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator — bottom */}
        <div style={{
          position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)",
          zIndex: 3,
          display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
          opacity: vidPhase >= 4 ? 1 : 0,
          transition: "opacity 1s ease 1s"
        }}>
          <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.65rem", color: "rgba(255,255,255,0.5)", letterSpacing: "0.3em", textTransform: "uppercase" }}>Scroll</span>
          <div style={{
            width: 1, height: 36, background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.5), transparent)",
            animation: "scrollLine 2s ease-in-out infinite"
          }} />
        </div>
      </section>

      {/* ═══ ABOUT US (Two images + text layout) ═══ */}
      <section style={{ padding: "100px clamp(16px, 5vw, 80px)", background: "var(--bg-2)" }}>
        <div style={{
          maxWidth: 1300, margin: "0 auto",
          display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 60, alignItems: "center"
        }} className="two-col">
          {/* Left — Two overlapping images */}
          <Reveal direction="left">
            <div style={{ position: "relative", display: "flex", gap: 20, alignItems: "flex-start" }}>
              {/* Image 1 — taller, left */}
              <div className="img-shimmer" style={{
                flex: "1", borderRadius: 14, overflow: "hidden",
                boxShadow: "0 20px 50px rgba(0,0,0,0.4)",
                border: "1px solid var(--card-border)"
              }}>
                <img
                  src="https://images.unsplash.com/photo-1619405399517-d7fce0f13302?w=500&q=80"
                  alt="Car detailing PPF"
                  style={{ width: "100%", height: 380, objectFit: "cover", display: "block" }}
                />
              </div>
              {/* Image 2 — offset down, right */}
              <div style={{
                flex: "1", borderRadius: 14, overflow: "hidden", marginTop: 60,
                boxShadow: "0 20px 50px rgba(0,0,0,0.4)",
                border: "1px solid var(--card-border)"
              }}>
                <img
                  src="https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=500&q=80"
                  alt="Ceramic coating application"
                  style={{ width: "100%", height: 380, objectFit: "cover", display: "block" }}
                />
              </div>
            </div>
          </Reveal>

          {/* Right — Text content */}
          <Reveal delay={0.15} direction="right">
            <div>
              {/* Tag with red underline */}
              <div style={{ marginBottom: 16 }}>
                <p style={{
                  fontFamily: "'Poppins', sans-serif", fontSize: "0.78rem",
                  fontWeight: 400, letterSpacing: "0.35em", color: "var(--text)", textTransform: "uppercase",
                  display: "inline-block", paddingBottom: 8
                }}>ABOUT US</p>
                <div style={{ width: 50, height: 3, background: RED, borderRadius: 2 }} />
              </div>

              <h2 style={{
                fontFamily: "'Oswald', sans-serif",
                fontSize: "clamp(1.6rem, 3.5vw, 2.6rem)", fontWeight: 800,
                color: "var(--text)", lineHeight: 1.15, margin: "0 0 22px"
              }}>
                Restoring Lasting Shine,<br/>Inside and Out
              </h2>

              <p style={{
                fontFamily: "'Poppins', sans-serif", fontSize: "0.95rem", color: "var(--footer-text)",
                lineHeight: 1.75, marginBottom: 32, maxWidth: 520
              }}>
                At Onyx Ceramic, we're passionate about making every vehicle look its absolute best — inside and out. From restoring showroom-level gloss to deep-cleaning interiors, our expert team delivers flawless results with care and precision.
              </p>

              <button onClick={() => navigate("about")} style={{
                background: RED, color: "#fff", border: "none",
                padding: "14px 34px", borderRadius: 8,
                fontFamily: "'Oswald', sans-serif", fontSize: "0.78rem",
                fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
                cursor: "pointer", transition: "all 0.3s",
                boxShadow: "0 4px 20px rgba(230,57,70,0.3)"
              }}
              onMouseEnter={e => e.target.style.transform = "translateY(-2px)"}
              onMouseLeave={e => e.target.style.transform = "translateY(0)"}
              >Read More</button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ SERVICES CARDS (DetailR style) ═══ */}
      <section id="svc" style={{ padding: "100px clamp(16px, 5vw, 80px)", background: "var(--bg-1)" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>
          <Reveal direction="left"><SectionTag title="Our Premium Services" sub="Precision detailing designed for cars that deserve nothing but the best." /></Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, alignItems: "stretch" }} className="svc-grid">
            {SERVICES_LIST.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.08} direction="zoom-up">
                <div onClick={() => navigate(s.id)} style={{
                  background: "var(--bg-2)", border: "1px solid var(--card-border)", borderRadius: 16,
                  overflow: "hidden", cursor: "pointer", transition: "all 0.4s",
                  display: "flex", flexDirection: "column", height: "100%"
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(230,57,70,0.25)"; e.currentTarget.style.transform = "translateY(-6px)"; e.currentTarget.style.boxShadow = "0 20px 50px rgba(0,0,0,0.35)"; const btn = e.currentTarget.querySelector('.svc-btn'); if(btn) { btn.style.cssText += 'background: #E63946 !important; color: #fff !important;'; } }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--card-border)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; const btn = e.currentTarget.querySelector('.svc-btn'); if(btn) { btn.style.cssText += 'background: #1a1a1a !important; color: #fff !important;'; } }}>
                  
                  {/* Text content — fixed height */}
                  <div style={{ padding: "28px 24px 20px", textAlign: "center", height: 150, display: "flex", flexDirection: "column", justifyContent: "flex-start", overflow: "hidden" }}>
                    <h3 style={{
                      fontFamily: "'Oswald', sans-serif", fontSize: "1.25rem",
                      fontWeight: 700, color: "var(--text)", letterSpacing: "0.04em", marginBottom: 14, lineHeight: 1.25,
                      flexShrink: 0
                    }}>{s.title}</h3>
                    <p style={{
                      fontFamily: "'Poppins', sans-serif", fontSize: "0.82rem", color: "var(--footer-text)",
                      lineHeight: 1.6, overflow: "hidden"
                    }}>{s.desc}</p>
                  </div>

                  {/* Icon badge + Image — fixed height */}
                  <div style={{ position: "relative", height: 240, flexShrink: 0 }}>
                    {/* Floating icon badge */}
                    <div style={{
                      position: "absolute", top: -22, left: "50%", transform: "translateX(-50%)", zIndex: 2,
                      width: 44, height: 44, borderRadius: "50%", className: "icon-hover", 
                      background: "var(--bg-2)", border: `2px solid rgba(230,57,70,0.3)`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.4)"
                    }}>
                      {Icons[s.icon](20)}
                    </div>
                    {/* Service image */}
                    <img src={s.img} alt={s.title} style={{
                      width: "100%", height: "100%", objectFit: "cover", display: "block"
                    }} />
                    {/* Top gradient fade into card */}
                    <div style={{
                      position: "absolute", top: 0, left: 0, right: 0, height: "30%",
                      background: "linear-gradient(to bottom, var(--bg-2), transparent)",
                      pointerEvents: "none"
                    }} />
                  </div>

                  {/* GET SERVICE button */}
                  <div className="svc-btn" style={{
                    padding: "0", background: "var(--bg-0)", borderTop: "1px solid rgba(230,57,70,0.15)",
                    textAlign: "center", flexShrink: 0, transition: "all 0.3s"
                  }}>
                    <div style={{
                      padding: "16px 24px",
                      fontFamily: "'Oswald', sans-serif", fontSize: "0.78rem",
                      fontWeight: 700, color: "#fff", letterSpacing: "0.15em", textTransform: "uppercase",
                      transition: "all 0.3s"
                    }}>GET SERVICE</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ WHAT WE DO (Side image layout like DetailR) ═══ */}
      <section style={{ padding: "100px clamp(16px, 5vw, 80px)", background: "var(--bg-0)", position: "relative" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: `linear-gradient(90deg, transparent, rgba(230,57,70,0.15), transparent)` }} />
        <div style={{ maxWidth: 1300, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "center" }} className="two-col">
          <Reveal direction="left">
            <div style={{ position: "relative" }}>
              <div style={{ borderRadius: 16, overflow: "hidden", boxShadow: `0 30px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(230,57,70,0.1)` }}>
                <img src="https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=700&q=80" alt="Detailing" style={{ width: "100%", height: 460, objectFit: "cover" }} />
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, var(--bg-0) 0%, transparent 30%)" }} />
              </div>
              <div style={{ position: "absolute", bottom: -16, right: -16, background: RED, borderRadius: 12, padding: "18px 26px", boxShadow: `0 10px 30px rgba(230,57,70,0.35)` }}>
                <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: "1.5rem", fontWeight: 800, color: "#fff" }}>50+</p>
                <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.72rem", color: "rgba(255,255,255,0.8)", letterSpacing: "0.1em" }}>BRANDS SERVED</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <SectionTag tag="WHAT WE DO" title="Full-Service Detailing For Cars" align="left" />
            <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "1.03rem", color: "var(--text-soft)", lineHeight: 1.8, marginBottom: 28 }}>
              At Onyx Ceramic, we specialize in comprehensive car care services to keep your vehicle looking its best, both inside and out. With a commitment to excellence and a passion for cars, our team of certified technicians delivers top-notch results with every service we offer.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {[{ icon: Icons.award, label: "Certified Experts" },{ icon: Icons.layers, label: "Flexible Packages" },{ icon: Icons.sparkle, label: "Supreme Shine" },{ icon: Icons.shield, label: "Aftercare Support" }].map((f, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 11, padding: "13px 14px", background: "var(--bg-2)", borderRadius: 10, border: "1px solid var(--card-border)" }}>
                  <div style={{ flexShrink: 0 }}>{f.icon(22)}</div>
                  <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.88rem", fontWeight: 600, color: "var(--text-soft)" }}>{f.label}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 16, marginTop: 28, alignItems: "center" }}>
              <a href="tel:+919956456499" style={{ display: "flex", alignItems: "center", gap: 8, color: RED, fontFamily: "'Oswald', sans-serif", fontSize: "1.3rem", fontWeight: 700, textDecoration: "none" }}>{Icons.phone(20)} +91 99564 56499</a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ WHY CHOOSE US ═══ */}
      <section style={{ padding: "100px clamp(16px, 5vw, 80px)", background: "var(--bg-1)" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto" }}>
          <Reveal direction="left"><SectionTag title="Why Choose Onyx?" /></Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 16 }}>
            {[
              { icon: Icons.check, title: "OUR COMMITMENT", desc: "Dedication lies at the heart of our ethos. We are steadfast in our commitment to providing unparalleled service, continuously surpassing your expectations." },
              { icon: Icons.award, title: "STANDARD OF QUALITY", desc: "From the selection of premium imported products to cutting-edge techniques, we uphold the highest benchmarks of excellence in every facet." },
              { icon: Icons.users, title: "BUILDING TRUST", desc: "Trust forms the bedrock of our business. We operate with unwavering honesty, integrity, and transparency in every interaction." },
              { icon: Icons.wind, title: "DUST-FREE STUDIO", desc: "Our climate-controlled, dust-free bay ensures flawless coating application every single time. No shortcuts, no compromises." },
              { icon: Icons.shield, title: "ASSURED WARRANTY", desc: "Every coating and PPF service comes with a written warranty. We stand behind our work and your investment completely." },
              { icon: Icons.leaf, title: "SUSTAINABILITY", desc: "We employ eco-conscious practices, minimize water consumption, and use environment-friendly cleaning agents while delivering exemplary results." }
            ].map((c, i) => (
              <Reveal key={i} delay={i * 0.07} direction="zoom-up">
                <div style={{
                  display: "flex", gap: 18, padding: "26px 24px", background: "var(--bg-2)", borderRadius: 14,
                  border: "1px solid var(--card-border)", transition: "all 0.3s", cursor: "default"
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(230,57,70,0.2)"; e.currentTarget.style.background = "rgba(230,57,70,0.03)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--card-border)"; e.currentTarget.style.background = DARK3; }}>
                  <div style={{ width: 50, height: 50, borderRadius: 12, background: "rgba(230,57,70,0.06)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{c.icon(26)}</div>
                  <div>
                    <h4 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "0.82rem", fontWeight: 700, color: RED, letterSpacing: "0.12em", marginBottom: 7 }}>{c.title}</h4>
                    <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.88rem", color: "var(--text-muted)", lineHeight: 1.6 }}>{c.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ BRAND LOGOS MARQUEE ═══ */}
      <section style={{ padding: "70px 0", background: "var(--bg-0)", overflow: "hidden", borderTop: "1px solid var(--divider)", borderBottom: "1px solid var(--divider)" }}>
        <Reveal direction="up"><p style={{ textAlign: "center", fontFamily: "'Oswald', sans-serif", fontSize: "1.35rem", fontWeight: 700, color: "var(--text-faded)", letterSpacing: "0.3em", marginBottom: 36 }}>WE HAVE DONE DETAILING FOR</p></Reveal>
        
        {/* Row 1 — scrolling right */}
        <div style={{ position: "relative", overflow: "hidden", marginBottom: 12 }}>
          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 80, background: `linear-gradient(to right, var(--bg-0), transparent)`, zIndex: 2, pointerEvents: "none" }} />
          <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 80, background: `linear-gradient(to left, var(--bg-0), transparent)`, zIndex: 2, pointerEvents: "none" }} />
          <div style={{ display: "flex", gap: 14, animation: "marquee 50s linear infinite", whiteSpace: "nowrap" }}>
            {[...BRANDS, ...BRANDS].map((b, i) => (
              <div key={`r1-${i}`} style={{
                flexShrink: 0, display: "flex", alignItems: "center", gap: 14,
                padding: "16px 28px", borderRadius: 12,
                background: "var(--bg-2)", border: "1px solid var(--card-border)",
                transition: "all 0.3s"
              }}>
                <img src={b.logo} alt={b.name} style={{
                  width: 36, height: 36, objectFit: "contain",
                  filter: "var(--logo-filter, none)", opacity: 0.75
                }} />
                <span style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "0.78rem", fontWeight: 500, color: "var(--text-faded)",
                  letterSpacing: "0.1em", textTransform: "uppercase"
                }}>{b.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Row 2 — scrolling left (reverse) */}
        <div style={{ position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 80, background: `linear-gradient(to right, var(--bg-0), transparent)`, zIndex: 2, pointerEvents: "none" }} />
          <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 80, background: `linear-gradient(to left, var(--bg-0), transparent)`, zIndex: 2, pointerEvents: "none" }} />
          <div style={{ display: "flex", gap: 14, animation: "marquee 55s linear infinite reverse", whiteSpace: "nowrap" }}>
            {[...BRANDS.slice().reverse(), ...BRANDS.slice().reverse()].map((b, i) => (
              <div key={`r2-${i}`} style={{
                flexShrink: 0, display: "flex", alignItems: "center", gap: 14,
                padding: "16px 28px", borderRadius: 12,
                background: "var(--bg-2)", border: "1px solid var(--card-border)",
                transition: "all 0.3s"
              }}>
                <img src={b.logo} alt={b.name} style={{
                  width: 36, height: 36, objectFit: "contain",
                  filter: "var(--logo-filter, none)", opacity: 0.75
                }} />
                <span style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: "0.78rem", fontWeight: 500, color: "var(--text-faded)",
                  letterSpacing: "0.1em", textTransform: "uppercase"
                }}>{b.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ GALLERY TEASER ═══ */}
      <section style={{ padding: "100px clamp(16px, 5vw, 80px)", background: "var(--bg-1)" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>
          <Reveal direction="left"><SectionTag tag="Gallery" title="Our Work Speaks For Itself" sub="See the Onyx transformation in action." /></Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14 }}>
            {[
              { src: "https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=600&q=80", label: "Ceramic Coating — Porsche 911" },
              { src: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=600&q=80", label: "PPF Installation — BMW M4" },
              { src: "https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=600&q=80", label: "Full Detail — Ferrari 488" },
            ].map((g, i) => (
              <Reveal key={i} delay={i * 0.1} direction="zoom-up">
                <div onClick={() => navigate("gallery")} style={{ position: "relative", borderRadius: 14, overflow: "hidden", aspectRatio: "16/10", cursor: "pointer" }}
                onMouseEnter={e => { e.currentTarget.querySelector("img").style.transform = "scale(1.06)"; e.currentTarget.querySelector(".ov").style.opacity = 1; }}
                onMouseLeave={e => { e.currentTarget.querySelector("img").style.transform = "scale(1)"; e.currentTarget.querySelector(".ov").style.opacity = 0; }}>
                  <img src={g.src} alt={g.label} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.6s" }} />
                  <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 50%)` }} />
                  <div className="ov" style={{ position: "absolute", inset: 0, background: "rgba(230,57,70,0.15)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.3s" }}>
                    <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.78rem", fontWeight: 700, color: "#fff", letterSpacing: "0.15em", textTransform: "uppercase", padding: "10px 24px", border: "2px solid rgba(255,255,255,0.7)", borderRadius: 6, background: "rgba(0,0,0,0.3)", backdropFilter: "blur(4px)" }}>View Gallery</span>
                  </div>
                  <p style={{ position: "absolute", bottom: 16, left: 20, fontFamily: "'Poppins', sans-serif", fontSize: "0.88rem", fontWeight: 600, color: "#fff", textShadow: "0 2px 8px rgba(0,0,0,0.8), 0 0 16px rgba(0,0,0,0.5)" }}>{g.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ GOOGLE REVIEWS ═══ */}
      <section style={{ padding: "100px clamp(16px, 5vw, 80px)", background: "var(--bg-0)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Reveal direction="left"><SectionTag tag="Reviews" title="What Our Clients Say" /></Reveal>
          
          {/* Overall Rating Badge */}
          <Reveal delay={0.05}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginBottom: 36 }}>
              <svg width="28" height="28" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              <span style={{ fontFamily: "'Oswald', sans-serif", fontSize: "1.6rem", fontWeight: 700, color: "var(--text)" }}>4.9</span>
              <div style={{ display: "flex", gap: 2 }}>{[1,2,3,4,5].map(i => <span key={i} style={{ color: "#FBBC05", fontSize: "18px" }}>★</span>)}</div>
              <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.85rem", color: "var(--text-faded)" }}>based on Google Reviews</span>
            </div>
          </Reveal>

          {/* Reviews Scrollable Cards */}
          <Reveal delay={0.1}>
            <div style={{ position: "relative" }}>
              <div id="reviewScroll" style={{
                display: "flex", gap: 16, overflowX: "auto", scrollBehavior: "smooth",
                paddingBottom: 12, scrollbarWidth: "none", msOverflowStyle: "none"
              }}>
                <style>{`#reviewScroll::-webkit-scrollbar { display: none; }`}</style>
                {TESTIMONIALS.map((t, i) => (
                  <div key={i} style={{
                    minWidth: 300, maxWidth: 320, flex: "0 0 auto",
                    background: "var(--bg-2)", border: "1px solid var(--card-border)", borderRadius: 14,
                    padding: "24px 22px", display: "flex", flexDirection: "column", gap: 14,
                    transition: "border-color 0.3s"
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(230,57,70,0.2)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "var(--card-border)"}>
                    {/* Profile header */}
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{
                        width: 44, height: 44, borderRadius: "50%",
                        background: `linear-gradient(135deg, ${["#E63946","#4285F4","#34A853","#FBBC05"][i % 4]}, ${["#c0392b","#1a73e8","#1e8e3e","#e8a400"][i % 4]})`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontFamily: "'Poppins', sans-serif", fontSize: "1.1rem", fontWeight: 700, color: "#fff"
                      }}>{t.name.charAt(0)}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.88rem", fontWeight: 600, color: "var(--text)" }}>{t.name}</span>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="#4285F4"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/><circle cx="12" cy="12" r="12" fill="#4285F4" opacity="0.15"/><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="#4285F4"/></svg>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.7rem", color: "var(--text-faded)" }}>{t.car}</span>
                        </div>
                      </div>
                    </div>

                    {/* Stars */}
                    <div style={{ display: "flex", gap: 2 }}>
                      {[...Array(t.rating)].map((_, j) => <span key={j} style={{ color: "#FBBC05", fontSize: "16px" }}>★</span>)}
                    </div>

                    {/* Review text */}
                    <p style={{
                      fontFamily: "'Poppins', sans-serif", fontSize: "0.86rem", color: "var(--text-muted)",
                      lineHeight: 1.65, flex: 1,
                      display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden"
                    }}>{t.text}</p>

                    {/* Read more link */}
                    <a href="https://www.google.com/maps/place/Onyx+Ceramic+Car+Care/data=!4m2!3m1!1s0x0:0xc7cf61d782721d3" target="_blank" rel="noopener noreferrer"
                      style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.78rem", fontWeight: 600, color: "#4285F4", textDecoration: "none" }}>
                      Read more
                    </a>
                  </div>
                ))}
              </div>

              {/* Scroll arrows */}
              <button onClick={() => document.getElementById('reviewScroll').scrollBy({ left: -336, behavior: 'smooth' })} style={{
                position: "absolute", left: -18, top: "50%", transform: "translateY(-50%)",
                width: 36, height: 36, borderRadius: "50%", border: "1px solid var(--card-border)",
                background: "var(--bg-2)", color: "var(--text-faded)", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.3s",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = RED}
              onMouseLeave={e => e.currentTarget.style.borderColor = "var(--card-border)"}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <button onClick={() => document.getElementById('reviewScroll').scrollBy({ left: 336, behavior: 'smooth' })} style={{
                position: "absolute", right: -18, top: "50%", transform: "translateY(-50%)",
                width: 36, height: 36, borderRadius: "50%", border: "1px solid var(--card-border)",
                background: "var(--bg-2)", color: "var(--text-faded)", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.3s",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = RED}
              onMouseLeave={e => e.currentTarget.style.borderColor = "var(--card-border)"}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
          </Reveal>

          {/* Write a review CTA */}
          <Reveal delay={0.2}>
            <div style={{ textAlign: "center", marginTop: 32 }}>
              <a href="https://www.google.com/maps/place/Onyx+Ceramic+Car+Care/data=!4m2!3m1!1s0x0:0xc7cf61d782721d3" target="_blank" rel="noopener noreferrer"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "12px 28px", borderRadius: 8,
                  background: "var(--input-bg)", border: "1px solid var(--card-border)",
                  color: "var(--text-soft)", fontFamily: "'Poppins', sans-serif", fontSize: "0.82rem", fontWeight: 600,
                  textDecoration: "none", transition: "all 0.3s", letterSpacing: "0.05em"
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#4285F4"; e.currentTarget.style.color = "#4285F4"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--card-border)"; e.currentTarget.style.color = "var(--text-soft)"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Write a Review on Google
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ FAQ SECTION ═══ */}
      <FAQSection />

      {/* ═══ INSTAGRAM / SOCIAL CTA ═══ */}
      <section style={{ padding: "90px clamp(16px, 5vw, 80px)", background: "var(--bg-1)", overflow: "hidden" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", textAlign: "center" }}>
          <Reveal direction="left"><SectionTag tag="Instagram" title="Follow Our Journey" sub="See our latest work and transformations on social media." /></Reveal>
          
          <Reveal delay={0.1}>
            {/* Instagram Grid — Beautiful hover cards */}
            <div className="ig-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 32 }}>
              {[
                { img: "./assets/images/ppf-service.jpg", label: "PPF Installation", likes: "124" },
                { img: "./assets/images/ceramic-service.jpg", label: "Paint Correction", likes: "98" },
                { img: "./assets/images/interior-service.jpg", label: "Interior Detailing", likes: "156" },
                { img: "https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=400&q=80", label: "Ceramic Coating", likes: "203" },
                { img: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=400&q=80", label: "Full Detail", likes: "187" },
                { img: "https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=400&q=80", label: "Graphene Coating", likes: "142" },
                { img: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=400&q=80", label: "Premium Wash", likes: "89" },
                { img: "https://images.unsplash.com/photo-1489824904134-891ab64532f1?w=400&q=80", label: "Sun Film", likes: "76" },
              ].map((item, i) => (
                <a key={i} href="https://instagram.com/onyxceramiccarcare" target="_blank" rel="noopener noreferrer" style={{
                  position: "relative", aspectRatio: "1", borderRadius: 10, overflow: "hidden",
                  display: "block", cursor: "pointer"
                }}
                onMouseEnter={e => { 
                  e.currentTarget.querySelector('.ig-over').style.opacity = 1;
                  e.currentTarget.querySelector('img').style.transform = 'scale(1.1)';
                }}
                onMouseLeave={e => { 
                  e.currentTarget.querySelector('.ig-over').style.opacity = 0;
                  e.currentTarget.querySelector('img').style.transform = 'scale(1)';
                }}>
                  <img src={item.img} alt={item.label} style={{
                    width: "100%", height: "100%", objectFit: "cover",
                    transition: "transform 0.5s ease"
                  }} />
                  {/* Hover overlay */}
                  <div className="ig-over" style={{
                    position: "absolute", inset: 0,
                    background: "rgba(0,0,0,0.6)", backdropFilter: "blur(2px)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexDirection: "column", gap: 8,
                    opacity: 0, transition: "opacity 0.35s ease"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#fff" }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" stroke="none"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                        <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.85rem", fontWeight: 600 }}>{item.likes}</span>
                      </div>
                    </div>
                    <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.72rem", fontWeight: 500, color: "rgba(255,255,255,0.8)", letterSpacing: "0.05em" }}>{item.label}</span>
                  </div>
                  {/* Instagram icon badge */}
                  <div style={{
                    position: "absolute", top: 8, right: 8,
                    width: 28, height: 28, borderRadius: 6,
                    background: "rgba(0,0,0,0.4)", backdropFilter: "blur(6px)",
                    display: "flex", alignItems: "center", justifyContent: "center"
                  }}>
                    {Icons.instagram(14)}
                  </div>
                </a>
              ))}
            </div>
          </Reveal>

          {/* Social Links Bar */}
          <Reveal delay={0.2}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 14, flexWrap: "wrap"
            }}>
              <a href="https://instagram.com/onyxceramiccarcare" target="_blank" rel="noopener noreferrer" style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: "linear-gradient(135deg, #833AB4, #E1306C, #F77737)", color: "#fff",
                padding: "12px 28px", borderRadius: 8, textDecoration: "none",
                fontFamily: "'Poppins', sans-serif", fontSize: "0.82rem", fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase",
                boxShadow: "0 4px 20px rgba(225,48,108,0.3)", transition: "all 0.3s"
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 30px rgba(225,48,108,0.4)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(225,48,108,0.3)"; }}
              >{Icons.instagram(16)} Follow on Instagram</a>

              <a href="https://www.youtube.com/@OnyxCeramicCarCare" target="_blank" rel="noopener noreferrer" style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: "var(--input-bg)", color: "var(--text-soft)", border: "1px solid var(--card-border)",
                padding: "12px 24px", borderRadius: 8, textDecoration: "none",
                fontFamily: "'Poppins', sans-serif", fontSize: "0.82rem", fontWeight: 600,
                letterSpacing: "0.08em", textTransform: "uppercase", transition: "all 0.3s"
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#FF0000"; e.currentTarget.style.color = "#FF0000"; e.currentTarget.style.transform = "translateY(-3px)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--card-border)"; e.currentTarget.style.color = "var(--text-soft)"; e.currentTarget.style.transform = "translateY(0)"; }}
              >{Icons.youtube(16)} YouTube</a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ CTA BAR ═══ */}
      <section style={{ padding: "56px clamp(16px, 5vw, 80px)", background: "linear-gradient(135deg, #E63946, #c0392b)", textAlign: "center" }}>
        <Reveal direction="left">
          <h2 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "clamp(1.1rem, 2.8vw, 1.7rem)", fontWeight: 700, color: "#fff", marginBottom: 10 }}>Do You Need Help?</h2>
          <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "1rem", color: "rgba(255,255,255,0.8)", marginBottom: 22 }}>The Onyx team always stays true to its words. For any queries and assistance, Call Us.</p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="tel:+919956456499" style={{ display: "inline-flex", alignItems: "center", gap: 8, color: RED, background: "#fff", padding: "12px 28px", borderRadius: 8, textDecoration: "none", fontFamily: "'Poppins', sans-serif", fontSize: "0.83rem", fontWeight: 700, letterSpacing: "0.1em" }}>{Icons.phone(15)} Call Now</a>
            <button onClick={onBook} style={{ background: "transparent", color: "#fff", border: "2px solid rgba(255,255,255,0.5)", padding: "12px 28px", borderRadius: 8, fontFamily: "'Poppins', sans-serif", fontSize: "0.83rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", cursor: "pointer" }}className="btn-shine">Book Online</button>
          </div>
        </Reveal>
      </section>
    </>
  );
}

// ─── SERVICE PAGE TEMPLATE ───
function ServicePage({ title, tagline, desc, features, img, img2, externalLink, externalLabel }) {
  return (
    <section style={{ paddingTop: 110 }}>
      <div style={{ position: "relative", minHeight: 400, display: "flex", alignItems: "center", background: "var(--bg-0)", overflow: "hidden", padding: "80px clamp(16px, 5vw, 80px)" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${img})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.15 }} />
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to right, var(--bg-0) 40%, transparent 80%)` }} />
        <div style={{ position: "relative", maxWidth: 1400, margin: "0 auto", width: "100%" }}>
          <Reveal direction="left">
            <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.85rem", fontWeight: 400, letterSpacing: "0.25em", color: RED }}>OUR SERVICES</span>
            <h1 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "clamp(1.8rem, 4.5vw, 2.8rem)", fontWeight: 800, color: "var(--text)", lineHeight: 1.1, margin: "12px 0 14px" }}>{title}</h1>
            <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "1.08rem", color: RED, fontWeight: 600 }}>{tagline}</p>
          </Reveal>
        </div>
      </div>
      <div style={{ padding: "80px clamp(16px, 5vw, 80px)", background: "var(--bg-1)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "center" }} className="two-col">
          <Reveal direction="left">
            <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "1.03rem", color: "var(--text-soft)", lineHeight: 1.8, marginBottom: 22 }}>{desc}</p>
            {features.map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 0", borderBottom: "1px solid var(--divider)" }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: RED, flexShrink: 0 }} />
                <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.93rem", color: "var(--text-muted)", fontWeight: 500 }}>{f}</span>
              </div>
            ))}
            {externalLink && (
              <a href={externalLink} target="_blank" rel="noopener noreferrer" style={{
                display: "inline-flex", alignItems: "center", gap: 10, marginTop: 28,
                padding: "14px 32px", borderRadius: 8,
                background: "linear-gradient(135deg, #E63946, #c0392b)", color: "#fff",
                fontFamily: "'Poppins', sans-serif", fontSize: "0.88rem", fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase", textDecoration: "none",
                boxShadow: "0 4px 20px rgba(230,57,70,0.3)", transition: "all 0.3s"
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 30px rgba(230,57,70,0.4)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(230,57,70,0.3)"; }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                {externalLabel || "Visit Website"}
              </a>
            )}
          </Reveal>
          <Reveal delay={0.15}>
            <div style={{ borderRadius: 16, overflow: "hidden", aspectRatio: "4/3", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}>
              <img src={img2 || img} alt={title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function PPFPage() { return <ServicePage title="Paint Protection Film" tagline="Invisible Armor for Your Vehicle" desc="Our Paint Protection Film provides the ultimate shield for your vehicle's paintwork. Using cutting-edge self-healing thermoplastic urethane, our PPF absorbs impacts from rock chips, scratches, and environmental damage while remaining completely invisible. Applied by certified installers in our dust-free studio." features={["Self-healing technology — minor scratches disappear with heat","10-year manufacturer warranty on all installations","Optically clear, non-yellowing film","Full body, partial, or high-impact area packages","Hydrophobic top coat for easy maintenance","Custom-cut patterns for perfect fit on every panel"]} img="https://images.unsplash.com/photo-1619405399517-d7fce0f13302?w=800&q=80" img2="https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=800&q=80" />; }
function CeramicPage() { return <ServicePage title="Ceramic Coating" tagline="Mirror-Like Gloss. Unbeatable Protection." desc="Our professional-grade 9H and 10H ceramic coatings create a permanent bond with your vehicle's paint, delivering extraordinary depth of gloss and protection. The nano-ceramic layer repels water, dirt, UV rays, and chemical contaminants. Applied in a controlled environment with meticulous paint correction beforehand." features={["9H and 10H hardness rated coatings","Extreme hydrophobic water-beading effect","UV protection to prevent paint fading","Chemical resistance against bird droppings and tree sap","Multi-year durability with proper maintenance","Full paint correction included before application"]} img="https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=800&q=80" img2="https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=800&q=80" />; }
function CarWashPage() { return <ServicePage title="Premium Car Wash & Spa" tagline="Deep Clean. Total Rejuvenation." desc="Our premium wash goes far beyond a standard car wash. Using pH-balanced, wax-safe shampoos and the two-bucket method, we ensure your paint is treated with the utmost care. Includes thick foam pre-wash, gentle hand wash, underbody flush, iron fallout removal, and purified water final rinse." features={["Thick foam pre-wash for safe dirt removal","Two-bucket hand wash technique","Underbody and wheel arch deep cleaning","Iron decontamination and clay bar treatment","Purified water final rinse — zero water spots","Tyre dressing and glass cleaning included"]} img="https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?w=800&q=80" img2="https://images.unsplash.com/photo-1507136566006-cfc505b114fc?w=800&q=80" />; }
function InteriorPage() { return <ServicePage title="Interior Detailing" tagline="Revitalize Your Ride, Inside and Out." desc="Our interior detailing service is a complete rejuvenation of your cabin. We deep-clean every surface, condition leather, shampoo upholstery, sanitize vents, and eliminate odours — leaving your interior looking and smelling showroom-fresh." features={["Deep vacuum and steam cleaning","Leather conditioning and UV protection","Dashboard and console restoration","Carpet shampooing and stain removal","Odor elimination treatment","Air vent and crevice detailing"]} img="https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&q=80" />; }
function PaintCorrectionPage() { return <ServicePage title="Paint Correction" tagline="Restore the Original Factory Finish." desc="Our multi-stage paint correction process removes swirl marks, micro-scratches, oxidation, and other defects from your paintwork. Using precision machine polishers and premium compounds, we restore your paint to a mirror-like finish." features={["Multi-stage machine polishing","Swirl mark and scratch removal","Oxidation restoration","Paint depth gauge assessment","High-gloss finishing polish","Can be combined with ceramic coating"]} img="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800&q=80" img2="https://images.unsplash.com/photo-1542362567-b07e54358753?w=800&q=80" />; }
function GraphenePage() { return <ServicePage title="Graphene Coating" tagline="Next-Generation Surface Protection." desc="Graphene coatings represent the cutting edge of paint protection technology. Offering superior heat dissipation, reduced water spotting, and enhanced durability compared to traditional ceramic coatings, graphene is the choice for owners who want the absolute best." features={["Superior heat dissipation properties","Reduced water spotting vs ceramic","Enhanced scratch resistance","Anti-static surface — repels dust","Multi-year protection and durability","Can be layered over ceramic coating"]} img="https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=800&q=80" img2="https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=800&q=80" />; }
function SunFilmPage() { return <ServicePage title="Sun Film & Window Tinting" tagline="Beat the Heat. Protect Your Ride." desc="Our premium sun control window films provide excellent UV protection, superior heat rejection, glare reduction, and enhanced privacy for your vehicle. We use top-grade films that are durable, non-fading, and backed by warranty. Professional bubble-free installation on all car types — sedans, SUVs, hatchbacks, and luxury vehicles." features={["UV protection — blocks up to 99% of harmful UV rays","Heat rejection — keeps your cabin significantly cooler","Glare reduction for safer, comfortable driving","Enhanced privacy and security","Non-fading, scratch-resistant films","Professional bubble-free installation with warranty"]} img="https://images.unsplash.com/photo-1489824904134-891ab64532f1?w=800&q=80" img2="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&q=80" />; }
function FourByFourPage() { return <ServicePage title="4x4 Accessories & Off-Road Mods" tagline="Gear Up for Every Adventure." desc="Transform your SUV or off-road vehicle with our premium 4x4 accessories and modifications. From rugged bull bars and side steps to roof racks, LED lighting, and performance upgrades — we provide high-quality accessories that combine style with functionality. Expert installation by experienced technicians." features={["Bull bars and front guards for protection","Roof racks and cargo carriers","LED light bars and auxiliary lighting","Side steps and running boards","Snorkels and performance air intakes","Mud flaps, fender flares, and body armor"]} img="https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=800&q=80" img2="https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=800&q=80" externalLink="https://onyxindia4x4.com" externalLabel="Visit Onyx India 4x4" />; }

// ─── GALLERY PAGE ───
function GalleryPage() {
  const imgs = [
    { src: "./assets/gallery/studio-4.jpg", full: "./assets/gallery/studio-4.jpg", label: "Onyx Ceramic Car Care — Studio Front" },
    { src: "./assets/gallery/studio-3.jpg", full: "./assets/gallery/studio-3.jpg", label: "PPF Work in Progress — BYD Atto 3" },
    { src: "./assets/gallery/studio-1.jpg", full: "./assets/gallery/studio-1.jpg", label: "Studio Interior — Ambassador Classic" },
    { src: "./assets/gallery/studio-2.jpg", full: "./assets/gallery/studio-2.jpg", label: "Detailing Bay — Studio View" },
    { src: "https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=600&q=80", full: "https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=1400&q=90", label: "Ceramic Coating — Porsche 911" },
    { src: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=600&q=80", full: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1400&q=90", label: "PPF Installation — BMW M4" },
    { src: "https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=600&q=80", full: "https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=1400&q=90", label: "Paint Correction — Ferrari 488" },
    { src: "https://images.unsplash.com/photo-1542362567-b07e54358753?w=600&q=80", full: "https://images.unsplash.com/photo-1542362567-b07e54358753?w=1400&q=90", label: "Graphene Coating — Lamborghini" },
    { src: "./assets/images/ppf-service.jpg", full: "./assets/images/ppf-service.jpg", label: "PPF Application Close-Up" },
    { src: "./assets/images/ceramic-service.jpg", full: "./assets/images/ceramic-service.jpg", label: "Machine Polishing — Paint Correction" },
    { src: "./assets/images/interior-service.jpg", full: "./assets/images/interior-service.jpg", label: "Interior Leather Conditioning" },
    { src: "https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?w=600&q=80", full: "https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?w=1400&q=90", label: "Premium Car Wash" },
  ];
  
  const [lightbox, setLightbox] = useState(null);
  
  const openLightbox = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(null);
  const goNext = (e) => { e.stopPropagation(); setLightbox((lightbox + 1) % imgs.length); };
  const goPrev = (e) => { e.stopPropagation(); setLightbox((lightbox - 1 + imgs.length) % imgs.length); };

  // Keyboard navigation
  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") setLightbox((lightbox + 1) % imgs.length);
      if (e.key === "ArrowLeft") setLightbox((lightbox - 1 + imgs.length) % imgs.length);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox]);

  const navBtnStyle = {
    position: "absolute", top: "50%", transform: "translateY(-50%)",
    width: 48, height: 48, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.3)",
    background: "rgba(0,0,0,0.6)", backdropFilter: "blur(10px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", cursor: "pointer", transition: "all 0.3s", zIndex: 10
  };

  return (
    <section style={{ paddingTop: 110, background: "var(--bg-0)" }}>
      <div style={{ padding: "80px clamp(16px, 5vw, 80px)" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>
          <Reveal direction="left"><SectionTag tag="Portfolio" title="Our Work Gallery" sub="A showcase of our finest detailing work on luxury vehicles." /></Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 12 }}>
            {imgs.map((img, i) => (
              <Reveal key={i} delay={i * 0.05} direction="zoom-up">
                <div 
                  onClick={() => openLightbox(i)}
                  style={{ position: "relative", borderRadius: 12, overflow: "hidden", aspectRatio: "16/11", cursor: "pointer" }}
                  onMouseEnter={e => { e.currentTarget.querySelector("img").style.transform = "scale(1.05)"; e.currentTarget.querySelector(".g-ov").style.opacity = 1; }}
                  onMouseLeave={e => { e.currentTarget.querySelector("img").style.transform = "scale(1)"; e.currentTarget.querySelector(".g-ov").style.opacity = 0; }}>
                  <img src={img.src} alt={img.label} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.5s" }} />
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 30%, transparent 55%)" }} />
                  {/* Hover overlay with zoom icon */}
                  <div className="g-ov" style={{
                    position: "absolute", inset: 0, background: "rgba(230,57,70,0.12)",
                    display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8,
                    opacity: 0, transition: "opacity 0.3s"
                  }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
                    </svg>
                    <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.7rem", fontWeight: 600, color: "var(--text)", letterSpacing: "0.1em" }}>VIEW</span>
                  </div>
                  <p style={{ position: "absolute", bottom: 14, left: 18, fontFamily: "'Poppins', sans-serif", fontSize: "0.86rem", fontWeight: 600, color: "#fff", zIndex: 1, textShadow: "0 2px 8px rgba(0,0,0,0.8), 0 0 16px rgba(0,0,0,0.5)" }}>{img.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ LIGHTBOX MODAL ═══ */}
      {lightbox !== null && (
        <div 
          onClick={closeLightbox}
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "var(--overlay)", backdropFilter: "blur(20px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "40px 80px",
            animation: "fadeIn 0.3s ease"
          }}>
          
          {/* Close button */}
          <button onClick={closeLightbox} style={{
            position: "absolute", top: 24, right: 24,
            width: 44, height: 44, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.3)",
            background: "rgba(0,0,0,0.5)", color: "#fff", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.3s", zIndex: 10
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = RED; e.currentTarget.style.background = RED; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; e.currentTarget.style.background = "rgba(0,0,0,0.5)"; }}
          >{Icons.x(20)}</button>

          {/* Prev button */}
          <button onClick={goPrev} style={{ ...navBtnStyle, left: 20 }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = RED; e.currentTarget.style.background = RED; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; e.currentTarget.style.background = "rgba(0,0,0,0.6)"; }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>

          {/* Image */}
          <div onClick={e => e.stopPropagation()} style={{
            maxWidth: "85vw", maxHeight: "85vh", position: "relative",
            borderRadius: 12, overflow: "hidden",
            boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
            animation: "popIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
          }}>
            <img 
              src={imgs[lightbox].full} 
              alt={imgs[lightbox].label}
              style={{ 
                maxWidth: "85vw", maxHeight: "80vh", objectFit: "contain", display: "block",
                borderRadius: 12
              }} 
            />
            {/* Caption bar */}
            <div style={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
              padding: "40px 24px 20px", display: "flex", justifyContent: "space-between", alignItems: "flex-end"
            }}>
              <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: "0.9rem", fontWeight: 700, color: "var(--text)", letterSpacing: "0.05em" }}>
                {imgs[lightbox].label}
              </p>
              <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.75rem", color: "var(--text-faded)" }}>
                {lightbox + 1} / {imgs.length}
              </p>
            </div>
          </div>

          {/* Next button */}
          <button onClick={goNext} style={{ ...navBtnStyle, right: 20 }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = RED; e.currentTarget.style.background = RED; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; e.currentTarget.style.background = "rgba(0,0,0,0.6)"; }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      )}
    </section>
  );
}

// ─── ABOUT PAGE ───
function AboutPage() {
  return (
    <section style={{ paddingTop: 110, background: "var(--bg-0)" }}>
      
      {/* Hero Banner */}
      <div style={{
        position: "relative", padding: "80px clamp(16px, 5vw, 80px) 60px",
        background: "var(--bg-0)",
        textAlign: "center", overflow: "hidden"
      }}>
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "url(https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=1400&q=80)",
          backgroundSize: "cover", backgroundPosition: "center", opacity: 0.08
        }} />
        <div style={{ position: "relative", zIndex: 2 }}>
          <Reveal>
            <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.8rem", fontWeight: 400, letterSpacing: "0.35em", color: RED, marginBottom: 14 }}>ABOUT US</p>
            <h1 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "clamp(2rem, 5vw, 3.5rem)", fontWeight: 800, color: "var(--text)", lineHeight: 1.1 }}>
              The Ultimate Car Detailing Studio
            </h1>
          </Reveal>
        </div>
      </div>

      {/* Founder & Story */}
      <div style={{ padding: "100px clamp(16px, 5vw, 80px)", background: "var(--bg-2)" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 60, alignItems: "center" }} className="two-col">
          <Reveal direction="left">
            <div style={{ display: "flex", gap: 16 }}>
              <div style={{ flex: 1, borderRadius: 14, overflow: "hidden", boxShadow: "0 20px 50px rgba(0,0,0,0.4)", border: "1px solid var(--card-border)" }}>
                <img src="https://images.unsplash.com/photo-1619405399517-d7fce0f13302?w=500&q=80" alt="Onyx studio" style={{ width: "100%", height: 360, objectFit: "cover", display: "block" }} />
              </div>
              <div style={{ flex: 1, borderRadius: 14, overflow: "hidden", marginTop: 50, boxShadow: "0 20px 50px rgba(0,0,0,0.4)", border: "1px solid var(--card-border)" }}>
                <img src="https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=500&q=80" alt="Coating" style={{ width: "100%", height: 360, objectFit: "cover", display: "block" }} />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} direction="right">
            <div>
              <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.78rem", fontWeight: 400, letterSpacing: "0.3em", color: RED, marginBottom: 8 }}>OUR STORY</p>
              <h2 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "clamp(1.4rem, 3vw, 2.2rem)", fontWeight: 800, color: "var(--text)", lineHeight: 1.15, margin: "0 0 20px" }}>About Onyx Ceramic Car Care</h2>
              <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.93rem", color: "var(--footer-text)", lineHeight: 1.8, marginBottom: 16 }}>
                Onyx Ceramic Car Care was founded with a singular passion: to bring world-class automotive detailing to Hyderabad. We are a team of dedicated car enthusiasts committed to restoring vehicles to their original glory and beyond.
              </p>
              <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.93rem", color: "var(--footer-text)", lineHeight: 1.8, marginBottom: 16 }}>
                We use only the finest internationally sourced products and state-of-the-art equipment. Our experienced technicians provide a wide range of services — from ceramic coatings and PPF installation to premium washes and complete interior restoration.
              </p>
              <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.93rem", color: "var(--footer-text)", lineHeight: 1.8, marginBottom: 24 }}>
                Our dust-free, climate-controlled studio is equipped with professional-grade lighting and tools to ensure flawless results on every project. We take pride in our work, and our goal is always to exceed our customers' expectations.
              </p>
              <a href="tel:+919956456499" style={{ display: "flex", alignItems: "center", gap: 10, color: RED, fontFamily: "'Oswald', sans-serif", fontSize: "1.15rem", fontWeight: 700, textDecoration: "none" }}>
                {Icons.phone(18)} +91 99564 56499 - Balanagar
              </a>
            </div>
          </Reveal>
        </div>
      </div>


      {/* 4 Core Traits */}
      <div style={{ padding: "100px clamp(16px, 5vw, 80px)", background: "var(--bg-2)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Reveal><SectionTag title="4 Uncompromising Traits of Onyx" sub="What sets us apart from every other detailing studio." /></Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 20 }} className="two-col">
            {[
              { title: "Quality", desc: "We never compromise on quality — even at the cost of profit. Every coating, every film, every wash is performed to perfection using premium imported products and certified processes. We have a policy of free repeat service if any work doesn't meet our exacting standards. This commitment to excellence is why we've earned the trust of hundreds of luxury car owners.", color: RED },
              { title: "Transparency", desc: "No hidden fees, no upselling, no surprises. We provide detailed explanations of every service, honest assessments of what your vehicle actually needs, and upfront pricing before any work begins. We believe in educating our customers so they can make informed decisions about their vehicle's care.", color: "#4ECDC4" },
              { title: "Expertise", desc: "Our technicians are factory-trained and certified by leading international coating and PPF brands. With years of hands-on experience across hundreds of vehicles — from daily commuters to exotic supercars — our team has the knowledge and skill to handle any challenge. We continuously invest in training and the latest technologies.", color: "#FFD93D" },
              { title: "Customer First", desc: "Your satisfaction is our obsession. From the first consultation to long after you drive away, we provide dedicated aftercare support, maintenance guidance, and comprehensive warranty coverage on all services. We treat every car as if it were our own, and we build lasting relationships based on trust, respect, and exceptional results.", color: "#6C63FF" }
            ].map((trait, i) => (
              <Reveal key={i} delay={i * 0.1} direction="zoom-up">
                <div style={{
                  padding: "36px 32px", background: "var(--bg-0)", borderRadius: 16,
                  border: "1px solid var(--card-border)", transition: "all 0.4s",
                  position: "relative", overflow: "hidden"
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(230,57,70,0.2)"; e.currentTarget.style.transform = "translateY(-4px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--card-border)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                  <div style={{ position: "absolute", top: 0, left: 0, width: 4, height: "100%", background: trait.color, borderRadius: "0 4px 4px 0" }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 10,
                      background: `${trait.color}12`, border: `1px solid ${trait.color}30`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "'Oswald', sans-serif", fontSize: "1.1rem",
                      fontWeight: 800, color: trait.color
                    }}>{i + 1}</div>
                    <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "1.15rem", fontWeight: 700, color: "var(--text)" }}>{trait.title}</h3>
                  </div>
                  <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.88rem", color: "var(--footer-text)", lineHeight: 1.7, paddingLeft: 58 }}>{trait.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* Feature Cards */}
      <div style={{ padding: "80px clamp(16px, 5vw, 80px)", background: "var(--bg-0)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }} className="svc-grid">
            {[
              { icon: Icons.award, title: "Certified Experts", desc: "Factory-trained technicians certified by leading international coating and PPF brands with years of hands-on experience.", img: "https://images.unsplash.com/photo-1619405399517-d7fce0f13302?w=400&q=80" },
              { icon: Icons.layers, title: "Flexible Packages", desc: "Customizable packages tailored to your vehicle's specific needs, preferences, and budget. Every car is unique.", img: "https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=400&q=80" },
              { icon: Icons.sparkle, title: "Supreme Shine Guaranteed", desc: "We guarantee showroom-level results. If the finish doesn't meet our standards, we redo it — no questions asked.", img: "https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=400&q=80" }
            ].map((f, i) => (
              <Reveal key={i} delay={i * 0.1} direction="zoom-up">
                <div style={{
                  background: "var(--bg-2)", border: "1px solid var(--card-border)", borderRadius: 16,
                  overflow: "hidden", transition: "all 0.4s", height: "100%", display: "flex", flexDirection: "column"
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(230,57,70,0.2)"; e.currentTarget.style.transform = "translateY(-5px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--card-border)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                  <div style={{ padding: "30px 26px", textAlign: "center", flex: "1" }}>
                    <div style={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(230,57,70,0.06)", border: "1px solid rgba(230,57,70,0.15)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>{f.icon(28)}</div>
                    <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "1rem", fontWeight: 700, color: "var(--text)", marginBottom: 12 }}>{f.title}</h3>
                    <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.85rem", color: "var(--footer-text)", lineHeight: 1.65 }}>{f.desc}</p>
                  </div>
                  <div style={{ height: 200, overflow: "hidden" }}>
                    <img src={f.img} alt={f.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* Welcome Section */}
      <div style={{ padding: "80px clamp(16px, 5vw, 80px)", background: "var(--bg-2)" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <Reveal>
            <h2 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "clamp(1.2rem, 3vw, 1.8rem)", fontWeight: 700, color: "var(--text)", lineHeight: 1.3, marginBottom: 12 }}>Looking for Complete Car Care Services?</h2>
            <h2 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "clamp(1.3rem, 3vw, 2rem)", fontWeight: 800, color: RED, lineHeight: 1.2, marginBottom: 28 }}>You Are in the Right Place!</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.95rem", color: "var(--footer-text)", lineHeight: 1.8, marginBottom: 20 }}>
              At Onyx Ceramic Car Care, we are a team of dedicated car enthusiasts passionate about restoring vehicles to their original glory. Our goal is to provide high-quality detailing services at competitive prices to keep your car looking its absolute best.
            </p>
            <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.93rem", color: "var(--text-dim)", lineHeight: 1.8, marginBottom: 16 }}>
              We use only the finest internationally sourced products and state-of-the-art equipment to ensure that your car receives the care it deserves. Our experienced technicians are trained to provide a wide range of detailing services — from PPF and ceramic coatings to comprehensive interior and exterior treatments. We take pride in our work, and our goal is always to exceed expectations.
            </p>
            <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.93rem", color: "var(--text-dim)", lineHeight: 1.8, marginBottom: 16 }}>
              Our dust-free, climate-controlled studio in Balanagar is purpose-built for precision work. Professional-grade lighting ensures every imperfection is visible during paint correction, and our controlled environment eliminates contamination during coating application — delivering results that simply aren't possible in an open workshop.
            </p>
            <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.93rem", color: "var(--text-dim)", lineHeight: 1.8 }}>
              In addition to our exceptional detailing services, we provide dedicated aftercare support, maintenance guidance, and comprehensive warranty coverage on all our services. We also value convenience — offering flexible scheduling and transparent communication throughout the process. Thank you for considering Onyx Ceramic for all of your car care needs. We look forward to bringing out the best in your vehicle.
            </p>
          </Reveal>
        </div>

        {/* Services we provide */}
        <div style={{ maxWidth: 900, margin: "40px auto 0" }}>
          <Reveal delay={0.2}>
            <div style={{ background: "var(--bg-0)", borderRadius: 14, border: "1px solid var(--card-border)", padding: "32px 36px" }}>
              <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "1.1rem", fontWeight: 700, color: "var(--text)", marginBottom: 18, textAlign: "center" }}>The Services We Provide</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }} className="svc-grid">
                {["Paint Protection Film (PPF)", "Ceramic Coating (9H/10H)", "Graphene Coating", "Premium Car Wash & Spa", "Interior Deep Cleaning", "Paint Correction & Polishing"].map((svc, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "var(--bg-2)", borderRadius: 8, border: "1px solid var(--card-border)" }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: RED, flexShrink: 0 }} />
                    <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.84rem", color: "var(--text-soft)" }}>{svc}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* ═══ MISSION, VISION & VALUES ═══ */}
      <div style={{ padding: "100px clamp(16px, 5vw, 80px)", background: "var(--bg-0)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Reveal><SectionTag title="Our Mission, Vision & Values" sub="The guiding principles behind everything we do at Onyx Ceramic." /></Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }} className="svc-grid">
            {[
              {
                icon: () => <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
                title: "Our Mission",
                desc: "To provide world-class car detailing services that protect, restore, and enhance every vehicle we touch. We strive to exceed expectations through premium products, certified expertise, and an unwavering commitment to perfection — making professional-grade car care accessible to every car owner in Hyderabad."
              },
              {
                icon: () => <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
                title: "Our Vision",
                desc: "To become Hyderabad's most trusted and recognized car detailing studio — known for unmatched quality, innovation, and customer satisfaction. We aim to set the benchmark for automotive care in India and expand our legacy of excellence to serve more vehicle owners across the region."
              },
              {
                icon: () => <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="1.5" strokeLinecap="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
                title: "Our Values",
                desc: "Quality without compromise — we never cut corners. Honesty and transparency in every interaction. Customer-first approach in everything we do. Continuous learning and improvement. Building lasting relationships based on trust. Eco-conscious practices that protect both your car and the environment."
              }
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.12} direction="zoom-up">
                <div style={{
                  padding: "40px 30px", background: "var(--bg-2)", borderRadius: 16,
                  border: "1px solid var(--card-border)", textAlign: "center",
                  transition: "all 0.4s", height: "100%",
                  display: "flex", flexDirection: "column", position: "relative", overflow: "hidden"
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(230,57,70,0.25)"; e.currentTarget.style.transform = "translateY(-6px)"; e.currentTarget.style.boxShadow = "0 20px 50px rgba(0,0,0,0.3)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--card-border)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
                  {/* Top red accent */}
                  <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: 60, height: 3, background: RED, borderRadius: "0 0 3px 3px" }} />
                  <div style={{
                    width: 72, height: 72, borderRadius: "50%", background: "rgba(230,57,70,0.06)",
                    border: "1px solid rgba(230,57,70,0.15)", display: "flex", alignItems: "center",
                    justifyContent: "center", margin: "0 auto 22px"
                  }}>{item.icon()}</div>
                  <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "1.25rem", fontWeight: 700, color: "var(--text)", marginBottom: 18 }}>{item.title}</h3>
                  <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.88rem", color: "var(--footer-text)", lineHeight: 1.75, flex: 1 }}>{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ padding: "60px clamp(16px, 5vw, 80px)", background: "var(--bg-0)" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }} className="svc-grid">
            {[["500","+","Cars Detailed"],["50","+","Brands Served"],["100","%","Client Satisfaction"],["50","+","5-Star Reviews"]].map(([n,s,l], i) => (
              <Reveal key={i} delay={i * 0.08} direction="zoom-up">
                <div style={{ textAlign: "center", padding: "30px 16px", background: "var(--bg-2)", border: "1px solid rgba(230,57,70,0.1)", borderRadius: 14 }}>
                  <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: "2rem", fontWeight: 800, color: RED, marginBottom: 4 }}><AnimatedCounter end={n} suffix={s} /></p>
                  <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.82rem", color: "var(--footer-text)", letterSpacing: "0.08em" }}>{l}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div style={{ padding: "80px clamp(16px, 5vw, 80px)", background: "var(--bg-2)" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <Reveal><SectionTag tag="WHAT WE DO" title="Detailing Services" sub="Provided with experience, care, precision and expertise." /></Reveal>
          <Reveal delay={0.1}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }} className="two-col">
              {[
                { title: "Paint Protection Film (PPF)", desc: "Invisible self-healing film that shields paint from scratches, rock chips, and UV damage. Full body, partial, and high-impact packages with 10-year warranty." },
                { title: "Ceramic Coating (9H/10H)", desc: "Nano-ceramic coatings that bond permanently for extreme hydrophobic properties, UV protection, chemical resistance, and mirror-like gloss lasting years." },
                { title: "Graphene Coating", desc: "Next-generation protection with superior heat dissipation, anti-static dust repelling, reduced water spotting, and enhanced scratch resistance." },
                { title: "Premium Car Wash & Spa", desc: "pH-balanced foam pre-wash, two-bucket hand wash, iron decontamination, clay bar treatment, underbody flush, and purified water rinse." },
                { title: "Interior Detailing", desc: "Deep steam cleaning, leather conditioning, dashboard restoration, carpet shampooing, stain removal, odor elimination, and air vent detailing." },
                { title: "Paint Correction", desc: "Multi-stage machine polishing to remove swirl marks, micro-scratches, oxidation, and restore factory-perfect mirror clarity." }
              ].map((s, i) => (
                <div key={i} style={{ padding: "26px 24px", background: "var(--bg-0)", borderRadius: 14, border: "1px solid var(--card-border)", transition: "border-color 0.3s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(230,57,70,0.15)"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "var(--card-border)"}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: RED, flexShrink: 0 }} />
                    <h4 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "0.92rem", fontWeight: 700, color: "var(--text)" }}>{s.title}</h4>
                  </div>
                  <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.85rem", color: "var(--footer-text)", lineHeight: 1.65, paddingLeft: 18 }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      {/* Contact Form + Info */}
      <div style={{ padding: "80px clamp(16px, 5vw, 80px)", background: "var(--bg-0)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 50 }} className="two-col">
          <Reveal direction="left">
            <div style={{ background: "var(--bg-2)", border: "1px solid var(--card-border)", borderRadius: 16, padding: "36px 32px" }}>
              <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.78rem", fontWeight: 400, letterSpacing: "0.3em", color: RED, marginBottom: 8 }}>CONTACT FORM</p>
              <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "1.2rem", fontWeight: 700, color: "var(--text)", marginBottom: 24 }}>Get In Touch</h3>
              {(() => {
                const inp = { width: "100%", background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: 8, padding: "13px 16px", color: "var(--text)", fontFamily: "'Poppins', sans-serif", fontSize: "0.9rem", outline: "none", boxSizing: "border-box" };
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
                    <input placeholder="Your Name" style={inp} />
                    <input placeholder="Phone Number" type="tel" style={inp} />
                    <select style={{ ...inp, color: "var(--text-faded)", appearance: "none" }}>
                      <option value="">— What are you looking for? —</option>
                      {["PPF","Ceramic Coating","Graphene Coating","Premium Wash","Interior Detailing","Paint Correction"].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button style={{ background: "linear-gradient(135deg, #E63946, #c0392b)", color: "#ffffff", border: "none", padding: "14px", borderRadius: 8, fontFamily: "'Oswald', sans-serif", fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", boxShadow: "0 4px 20px rgba(230,57,70,0.3)", marginTop: 4 }}>Submit Form</button>
                  </div>
                );
              })()}
            </div>
          </Reveal>
          <Reveal delay={0.15} direction="right">
            <div>
              <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.78rem", fontWeight: 400, letterSpacing: "0.3em", color: RED, marginBottom: 8 }}>CONTACT US</p>
              <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "1.2rem", fontWeight: 700, color: "var(--text)", marginBottom: 8 }}>Have Questions?</h3>
              <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "1.5rem", fontWeight: 800, color: "var(--text)", marginBottom: 30 }}>Get In Touch!</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {[
                  { icon: Icons.phone, title: "Phone", text: "+91 99564 56499", href: "tel:+919956456499" },
                  { icon: Icons.mail, title: "Email", text: "info@onyxceramiccarcare.com", href: "mailto:info@onyxceramiccarcare.com" },
                  { icon: Icons.mapPin, title: "Studio", text: "IDA, 5/A Opposite IDPL Research Centre, SVCIE, Balanagar, Hyderabad, Telangana 500037", href: "https://www.google.com/maps/place/Onyx+Ceramic+Car+Care/data=!4m2!3m1!1s0x0:0xc7cf61d782721d3" }
                ].map((c, i) => (
                  <a key={i} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined} style={{ display: "flex", gap: 14, padding: "18px 20px", background: "var(--bg-2)", border: "1px solid var(--card-border)", borderRadius: 12, textDecoration: "none", transition: "border-color 0.3s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(230,57,70,0.3)"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "var(--card-border)"}>
                    <div style={{ width: 42, height: 42, borderRadius: 10, background: "rgba(230,57,70,0.07)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: RED }}>{c.icon(18)}</div>
                    <div>
                      <h4 style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.72rem", fontWeight: 400, color: RED, letterSpacing: "0.15em", marginBottom: 4 }}>{c.title}</h4>
                      <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.9rem", color: "var(--text-soft)", lineHeight: 1.5 }}>{c.text}</p>
                    </div>
                  </a>
                ))}
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
                {[["https://instagram.com/onyxceramiccarcare", Icons.instagram],["https://www.facebook.com/61560733641254", Icons.facebook],["https://www.youtube.com/@OnyxCeramicCarCare", Icons.youtube],["https://x.com/Onyxceramic", Icons.twitter],["https://www.reddit.com/user/onyxceramiccarcare/", Icons.reddit],["https://www.linkedin.com/company/onyxceramiccarcare/", Icons.linkedin]].map(([url, icon], i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{ width: 42, height: 42, borderRadius: 10, border: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--footer-text)", transition: "all 0.3s", textDecoration: "none" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = RED; e.currentTarget.style.color = RED; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "var(--footer-text)"; }}
                  >{icon(17)}</a>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>

    </section>
  );
}


// ─── CONTACT PAGE ───
function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [done, setDone] = useState(false);
  const inp = { width: "100%", background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: 8, padding: "13px 16px", color: "var(--text)", fontFamily: "'Poppins', sans-serif", fontSize: "0.93rem", outline: "none", boxSizing: "border-box", transition: "border-color 0.3s" };
  return (
    <section style={{ paddingTop: 110, background: "var(--bg-0)" }}>
      <div style={{ padding: "80px clamp(16px, 5vw, 80px)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Reveal direction="left"><SectionTag tag="Contact Us" title="Have Questions? Get In Touch!" /></Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }} className="two-col">
            <Reveal direction="left">
              <div style={{ background: "var(--bg-2)", border: "1px solid var(--card-border)", borderRadius: 16, padding: "38px 32px" }}>
                {done ? (
                  <div style={{ textAlign: "center", padding: "36px 0" }}>
                    <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(230,57,70,0.1)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>{Icons.check(28)}</div>
                    <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "1.25rem", color: "var(--text)", marginBottom: 6 }}>Thank You!</h3>
                    <p style={{ fontFamily: "'Poppins', sans-serif", color: "var(--text-muted)" }}>We'll get back to you shortly.</p>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
                    <input placeholder="Your Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} style={inp} onFocus={e => e.target.style.borderColor = "rgba(230,57,70,0.4)"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.2)"} />
                    <input placeholder="Email Address" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} style={inp} onFocus={e => e.target.style.borderColor = "rgba(230,57,70,0.4)"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.2)"} />
                    <input placeholder="Phone Number" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} style={inp} onFocus={e => e.target.style.borderColor = "rgba(230,57,70,0.4)"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.2)"} />
                    <select value={form.service} onChange={e => setForm({...form, service: e.target.value})} style={{...inp, color: form.service ? "#fff" : "#777", appearance: "none"}}>
                      <option value="" style={{ background: "var(--bg-1)" }}>— Select Service —</option>
                      {["PPF Installation","Ceramic Coating","Premium Car Wash","Interior Detailing","Paint Correction","Graphene Coating"].map(s => <option key={s} value={s} style={{ background: "var(--bg-1)" }}>{s}</option>)}
                    </select>
                    <textarea placeholder="Your Message" rows={4} value={form.message} onChange={e => setForm({...form, message: e.target.value})} style={{...inp, resize: "vertical"}} onFocus={e => e.target.style.borderColor = "rgba(230,57,70,0.4)"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.2)"} />
                    <button onClick={() => setDone(true)} style={{
                      background: "linear-gradient(135deg, #E63946, #c0392b)", color: "#ffffff", border: "none",
                      padding: "14px", borderRadius: 8, fontFamily: "'Poppins', sans-serif", fontSize: "0.83rem",
                      fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", cursor: "pointer",
                      boxShadow: `0 4px 20px rgba(230,57,70,0.3)`, marginTop: 2
                    }}>Submit Form</button>
                  </div>
                )}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                {[
                  { icon: Icons.mapPin, title: "Studio Address", text: "IDA, 5/A Opposite IDPL Research Centre,\nSVCIE, Balanagar, Hyderabad,\nTelangana 500037", href: "https://www.google.com/maps/place/Onyx+Ceramic+Car+Care/data=!4m2!3m1!1s0x0:0xc7cf61d782721d3" },
                  { icon: Icons.phone, title: "Call Us", text: "+91 99564 56499", href: "tel:+919956456499" },
                  { icon: Icons.mail, title: "Email", text: "info@onyxceramiccarcare.com", href: "mailto:info@onyxceramiccarcare.com" }
                ].map((c, i) => (
                  <a key={i} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined} style={{ display: "flex", gap: 16, padding: "20px 22px", background: "var(--bg-2)", border: "1px solid var(--card-border)", borderRadius: 12, textDecoration: "none", transition: "border-color 0.3s, transform 0.3s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(230,57,70,0.3)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--card-border)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: "rgba(230,57,70,0.07)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: RED }}>{c.icon(18)}</div>
                    <div>
                      <h4 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "0.82rem", fontWeight: 700, color: RED, letterSpacing: "0.12em", marginBottom: 5 }}>{c.title}</h4>
                      <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.9rem", color: "var(--text-soft)", lineHeight: 1.5, whiteSpace: "pre-line" }}>{c.text}</p>
                    </div>
                  </a>
                ))}
                <div style={{ borderRadius: 12, overflow: "hidden", aspectRatio: "16/9", border: "1px solid var(--card-border)" }}>
                  <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1000!2d78.4375!3d17.4699!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0xc7cf61d782721d3!2sOnyx%20Ceramic%20Car%20Care!5e0!3m2!1sen!2sin!4v1"
                    width="100%" height="100%" style={{ border: 0, filter: "var(--map-filter)" }} allowFullScreen="" loading="lazy" title="Location" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── BLOG PAGE ───
const BLOG_POSTS = [
  {
    id: "ceramic-coating-vs-ppf",
    title: "Ceramic Coating vs PPF: Which Is Right for Your Car?",
    date: "April 20, 2026",
    excerpt: "Both protect your paint, but in very different ways. One is a chemical layer for gloss and hydrophobic properties, the other is a physical shield against rock chips and scratches.",
    img: "https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?w=600&q=80",
    tag: "Guide",
    content: `Choosing between ceramic coating and PPF is one of the most common questions car owners ask. Here's a detailed breakdown to help you make the right decision.

**What is Ceramic Coating?**
Ceramic coating is a liquid polymer that chemically bonds with your vehicle's factory paint. It creates a permanent or semi-permanent layer of protection that provides incredible gloss, hydrophobic water-beading, UV protection, and resistance to chemical stains and bird droppings. Our 9H and 10H ceramic coatings at Onyx Ceramic Car Care last 3-5 years with proper maintenance.

**What is PPF (Paint Protection Film)?**
PPF, also known as clear bra, is a thermoplastic urethane film that is physically applied to your car's painted surfaces. It acts as an invisible shield against rock chips, scratches, bug splatters, and minor abrasions. Modern PPF films are self-healing — light scratches disappear with heat exposure.

**Key Differences:**
• Protection type: Ceramic coating protects against chemical damage and UV rays. PPF protects against physical damage like rock chips and scratches.
• Appearance: Ceramic coating enhances gloss significantly. PPF is virtually invisible and maintains the original look.
• Durability: Ceramic coating lasts 3-5 years. PPF can last 7-10 years with proper care.
• Cost: Ceramic coating is more affordable. PPF costs more due to material and labor-intensive installation.
• Self-healing: Only PPF has self-healing properties. Ceramic coating does not.

**Can You Combine Both?**
Absolutely! Many of our customers at Onyx Ceramic Car Care opt for PPF on high-impact areas (front bumper, hood, fenders, mirrors) and ceramic coating over the entire vehicle including on top of the PPF. This combination gives you the best of both worlds — physical protection where you need it most, plus gloss and easy maintenance everywhere.

**Our Recommendation:**
If you're primarily concerned about maintaining a stunning appearance with easy cleaning, go with ceramic coating. If you regularly drive on highways or want maximum physical protection, PPF is the better choice. For the ultimate protection, combine both.

Visit us at Onyx Ceramic Car Care, Balanagar, Hyderabad or call +91 99564 56499 for a free consultation.`
  },
  {
    id: "how-to-maintain-ceramic-coating",
    title: "How to Maintain Your Ceramic Coating: Complete Guide",
    date: "April 15, 2026",
    excerpt: "A ceramic coating can last years, but only with the right maintenance routine. Learn the do's and don'ts to keep your coating performing at its best.",
    img: "https://images.unsplash.com/photo-1607860108855-64acf2078ed9?w=600&q=80",
    tag: "Tips",
    content: `Your ceramic coating is an investment in your vehicle's appearance and protection. Here's how to maintain it properly so it lasts as long as possible.

**The First 7 Days After Application**
• Do not wash your car for at least 7 days after ceramic coating application
• Avoid parking under trees where sap or bird droppings can land
• Do not use any spray detailers or quick waxes
• Keep the car in a garage if possible

**Regular Washing Routine**
• Wash your car every 2 weeks using the two-bucket method
• Use pH-neutral car shampoo only — avoid harsh detergents
• Never use automatic car washes with spinning brushes
• Use a microfiber wash mitt, not sponges
• Always wash from top to bottom
• Dry with a clean, plush microfiber towel or air blower

**What to Avoid**
• Harsh chemicals, acidic or alkaline cleaners
• Abrasive polishes or compounds (these will remove the coating)
• Automatic car washes with brushes
• Leaving bird droppings or tree sap for extended periods
• Using dirty towels or sponges that can introduce scratches

**Annual Maintenance**
• Consider a maintenance boost service every 12-18 months
• A professional decontamination wash removes embedded contaminants
• Inspection of the coating's hydrophobic performance

At Onyx Ceramic Car Care in Balanagar, Hyderabad, we provide aftercare guidance with every ceramic coating service. Contact us at +91 99564 56499 for maintenance services.`
  },
  {
    id: "benefits-of-ppf",
    title: "7 Benefits of Paint Protection Film You Didn't Know",
    date: "April 10, 2026",
    excerpt: "PPF does more than just protect against rock chips. Discover the hidden benefits that make paint protection film a smart investment for any car owner.",
    img: "https://images.unsplash.com/photo-1619405399517-d7fce0f13302?w=600&q=80",
    tag: "PPF",
    content: `Paint Protection Film (PPF) has evolved significantly in recent years. Here are 7 benefits that make it an essential investment for car owners.

**1. Self-Healing Technology**
Modern PPF films have a self-healing top coat. Light scratches and swirl marks disappear when exposed to heat — either from the sun or warm water. This keeps your car looking flawless without any intervention.

**2. Invisible Protection**
Today's premium PPF is virtually undetectable once installed properly. It doesn't alter your car's appearance — in fact, it enhances the paint's depth and clarity.

**3. UV Ray Protection**
PPF blocks harmful ultraviolet rays that cause paint oxidation and fading over time. This is especially important in Hyderabad's intense sun exposure.

**4. Stain Resistance**
PPF resists staining from bird droppings, tree sap, bug splatter, and hard water spots. These contaminants sit on the film's surface rather than bonding with the paint.

**5. Preserves Resale Value**
A car with PPF-protected original paint commands a higher resale value. Buyers appreciate knowing the paint underneath is in pristine condition.

**6. Easy Maintenance**
PPF-protected surfaces are easier to clean. Dirt and grime slide off more easily, reducing the frequency and effort of washing.

**7. Long-Term Cost Savings**
While PPF requires an upfront investment, it prevents costly paint repairs, touch-ups, and repainting. Over a 5-10 year ownership period, PPF can save you significant money.

Get professional PPF installation at Onyx Ceramic Car Care, Balanagar, Hyderabad. Call +91 99564 56499 for a free assessment.`
  },
  {
    id: "sun-film-benefits-hyderabad",
    title: "Why Every Car in Hyderabad Needs Sun Film: Heat & UV Protection",
    date: "April 5, 2026",
    excerpt: "Hyderabad's scorching summers make sun film essential. Learn how window tinting protects your car's interior, improves comfort, and reduces AC load.",
    img: "https://images.unsplash.com/photo-1489824904134-891ab64532f1?w=600&q=80",
    tag: "Sun Film",
    content: `Hyderabad's temperatures regularly cross 40°C in summer. Here's why sun film is not a luxury but a necessity for every car in the city.

**The Heat Problem**
Without sun film, your car's cabin temperature can reach 70-80°C when parked in direct sunlight. This damages your dashboard, fades upholstery, cracks leather seats, and makes the first 10 minutes of driving extremely uncomfortable.

**Benefits of Professional Sun Film:**
• Blocks up to 99% of harmful UV rays that cause skin damage and interior fading
• Rejects 40-70% of solar heat depending on the film grade
• Reduces glare for safer, more comfortable driving
• Enhances privacy and security
• Reduces AC load by 20-30%, improving fuel efficiency
• Protects leather and fabric interiors from cracking and fading

**Choosing the Right Sun Film:**
• Dyed films: Most affordable, good for privacy, moderate heat rejection
• Metallic films: Better heat rejection, but can interfere with phone signals
• Ceramic films: Premium option — excellent heat rejection without signal interference
• Carbon films: Good balance of performance and aesthetics

**Legal Considerations in India:**
As per Indian motor vehicle regulations, front windshield must allow 70% light transmission, and front side windows must allow 50%. Rear and back windows have more flexibility. Always choose films that comply with local regulations.

Visit Onyx Ceramic Car Care in Balanagar for professional sun film installation. Call +91 99564 56499.`
  },
  {
    id: "interior-detailing-guide",
    title: "Complete Interior Detailing Guide: What to Expect & Why It Matters",
    date: "March 28, 2026",
    excerpt: "Your car's interior collects dust, bacteria, and allergens daily. Professional interior detailing doesn't just clean — it sanitizes and restores.",
    img: "https://images.unsplash.com/photo-1507136566006-cfc505b114fc?w=600&q=80",
    tag: "Interior",
    content: `Most car owners focus on exterior appearance, but your car's interior is where you spend all your time. Here's why professional interior detailing matters.

**What Professional Interior Detailing Includes:**
• Deep vacuum cleaning of all surfaces, seats, carpets, and hard-to-reach areas
• Steam cleaning to sanitize and kill bacteria, viruses, and allergens
• Leather cleaning and conditioning to restore softness and prevent cracking
• Dashboard and trim restoration with UV-protectant dressing
• Door panel and center console deep cleaning
• Headliner spot cleaning
• Glass cleaning inside and out
• Air vent and AC duct cleaning
• Professional odor elimination treatment

**Why Regular Interior Detailing is Important:**
• Health: Your car's interior can harbor more bacteria than a toilet seat. Steering wheels, gear knobs, and door handles are high-touch surfaces that need regular sanitization
• Comfort: A clean, fresh-smelling interior makes every drive more enjoyable
• Preservation: Regular conditioning prevents leather cracking, dashboard fading, and trim deterioration
• Resale value: A well-maintained interior significantly boosts your car's resale value
• Allergens: Professional detailing removes pollen, dust mites, and pet dander

**How Often Should You Detail Your Interior?**
We recommend a thorough interior detailing every 3-6 months, depending on usage. If you have kids, pets, or eat in your car frequently, every 3 months is ideal.

Book your interior detailing at Onyx Ceramic Car Care, Balanagar, Hyderabad. Call +91 99564 56499.`
  },
  {
    id: "paint-correction-swirl-marks",
    title: "Paint Correction: How to Remove Swirl Marks & Restore Showroom Finish",
    date: "March 20, 2026",
    excerpt: "Those spider-web-like scratches ruining your paint? That's swirl marks from improper washing. Learn how paint correction eliminates them permanently.",
    img: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=600&q=80",
    tag: "Paint Care",
    content: `Swirl marks are the most common paint defect, and almost every car has them. Here's everything you need to know about paint correction.

**What Are Swirl Marks?**
Swirl marks are circular micro-scratches in your car's clear coat. They're most visible in direct sunlight, creating a spider-web pattern across your paint. They're caused by improper washing techniques, automatic car washes, dirty cloths, and poor drying methods.

**What is Paint Correction?**
Paint correction is a professional process of removing imperfections from your car's clear coat using machine polishing. It involves controlled removal of a microscopic layer of clear coat to level out scratches, swirl marks, water spots, and oxidation.

**Types of Paint Correction:**
• Single-stage: One polishing step for light swirl marks and minor scratches. Removes about 50-60% of defects
• Two-stage (compound + polish): Two polishing steps for moderate to heavy defects. Removes about 80-90% of defects
• Multi-stage: Three or more steps for severely damaged paint. Can remove 95%+ of defects

**After Paint Correction:**
Once your paint is corrected to a flawless finish, it's essential to protect it immediately with either ceramic coating or PPF. Without protection, new swirl marks will appear within weeks of regular washing.

**Prevention Tips:**
• Always use the two-bucket wash method
• Use high-quality microfiber mitts and towels
• Never use automatic car washes with spinning brushes
• Wash from top to bottom
• Apply ceramic coating for ongoing protection

Get professional paint correction at Onyx Ceramic Car Care, Balanagar. Call +91 99564 56499 for a free paint inspection.`
  }
];

function BlogPage() {
  const [selectedPost, setSelectedPost] = useState(null);

  if (selectedPost) {
    const post = BLOG_POSTS.find(p => p.id === selectedPost);
    return (
      <section style={{ paddingTop: 110, background: "var(--bg-0)" }}>
        <div style={{ padding: "60px clamp(16px, 5vw, 80px)" }}>
          <div style={{ maxWidth: 800, margin: "0 auto" }}>
            <button onClick={() => setSelectedPost(null)} style={{
              background: "none", border: "none", color: RED, cursor: "pointer",
              fontFamily: "'Poppins', sans-serif", fontSize: "0.88rem", fontWeight: 600,
              display: "flex", alignItems: "center", gap: 6, marginBottom: 24
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              Back to Blog
            </button>
            <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.75rem", fontWeight: 600, color: RED, letterSpacing: "0.1em", textTransform: "uppercase" }}>{post.tag}</span>
            <h1 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "clamp(1.3rem, 3vw, 1.8rem)", fontWeight: 700, color: "var(--text)", lineHeight: 1.25, margin: "10px 0 12px" }}>{post.title}</h1>
            <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.82rem", color: "var(--text-faded)", marginBottom: 28 }}>{post.date} • Onyx Ceramic Car Care</p>
            <div style={{ borderRadius: 14, overflow: "hidden", marginBottom: 32, aspectRatio: "16/9" }}>
              <img src={post.img} alt={post.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.95rem", color: "var(--text-soft)", lineHeight: 1.85 }}>
              {post.content.split('\n\n').map((para, i) => {
                if (para.startsWith('**') && para.endsWith('**')) {
                  return <h3 key={i} style={{ fontFamily: "'Oswald', sans-serif", fontSize: "1.05rem", fontWeight: 700, color: "var(--text)", margin: "28px 0 12px" }}>{para.replace(/\*\*/g, '')}</h3>;
                }
                if (para.startsWith('•')) {
                  return <div key={i} style={{ margin: "8px 0" }}>{para.split('\n').map((line, j) => (
                    <div key={j} style={{ display: "flex", gap: 10, marginBottom: 6, alignItems: "flex-start" }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: RED, flexShrink: 0, marginTop: 8 }} />
                      <span>{line.replace('• ', '')}</span>
                    </div>
                  ))}</div>;
                }
                const parts = para.split(/(\*\*.*?\*\*)/g);
                return <p key={i} style={{ marginBottom: 16 }}>{parts.map((part, j) => 
                  part.startsWith('**') ? <strong key={j} style={{ color: "var(--text)", fontWeight: 600 }}>{part.replace(/\*\*/g, '')}</strong> : part
                )}</p>;
              })}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section style={{ paddingTop: 110, background: "var(--bg-0)" }}>
      <div style={{ padding: "80px clamp(16px, 5vw, 80px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Reveal direction="left"><SectionTag tag="Blog" title="Car Care Insights & Tips" sub="Expert guides, detailing tips, and everything you need to know about protecting your vehicle." /></Reveal>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 22 }}>
            {BLOG_POSTS.map((post, i) => (
              <Reveal key={post.id} delay={i * 0.08} direction="zoom-up">
                <div onClick={() => setSelectedPost(post.id)} style={{
                  background: "var(--bg-2)", border: "1px solid var(--card-border)", borderRadius: 14,
                  overflow: "hidden", cursor: "pointer", transition: "all 0.4s"
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(230,57,70,0.25)"; e.currentTarget.style.transform = "translateY(-5px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--card-border)"; e.currentTarget.style.transform = "translateY(0)"; }}>
                  <div style={{ height: 200, overflow: "hidden" }}><img src={post.img} alt={post.title} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.5s" }} /></div>
                  <div style={{ padding: "22px 24px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.7rem", color: RED, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", background: "rgba(230,57,70,0.08)", padding: "3px 10px", borderRadius: 4 }}>{post.tag}</span>
                      <span style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.72rem", color: "var(--text-faded)" }}>{post.date}</span>
                    </div>
                    <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: "0.9rem", fontWeight: 700, color: "var(--text)", margin: "0 0 10px", lineHeight: 1.35 }}>{post.title}</h3>
                    <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.84rem", color: "var(--text-muted)", lineHeight: 1.6, marginBottom: 14 }}>{post.excerpt}</p>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, color: RED, fontFamily: "'Poppins', sans-serif", fontSize: "0.78rem", fontWeight: 700, letterSpacing: "0.08em" }}>
                      Read More <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={RED} strokeWidth="2" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── FAQ SECTION ───
const FAQ_DATA = [
  {
    q: "What is ceramic coating and how long does it last?",
    a: "Ceramic coating is a liquid nano-ceramic layer that bonds permanently to your car's paint, creating extreme gloss, hydrophobic water-beading, UV protection, and chemical resistance. Our professional 9H/10H ceramic coatings last 3-5 years with proper maintenance."
  },
  {
    q: "What is the difference between ceramic coating and PPF?",
    a: "Ceramic coating is a chemical layer that provides gloss, UV protection, and hydrophobic properties. PPF (Paint Protection Film) is a physical thermoplastic film that protects against rock chips, scratches, and physical damage with self-healing technology. Both can be combined for ultimate protection."
  },
  {
    q: "How much does PPF installation cost in Hyderabad?",
    a: "PPF pricing depends on vehicle size and coverage area — full body, front-end, or high-impact zones. We use premium self-healing TPU films. Contact us at +91 99564 56499 for a free quote. Our studio is in Balanagar, easily accessible from Kukatpally, KPHB, and Miyapur."
  },
  {
    q: "Do you provide sun film / window tinting services?",
    a: "Yes! We offer premium sun control window films with excellent UV protection, superior heat rejection, glare reduction, and enhanced privacy. Available for all car types — sedans, SUVs, and hatchbacks."
  },
  {
    q: "Where is your studio located?",
    a: "Onyx Ceramic Car Care is located at IDA, 5/A Opposite IDPL Research Centre, SVCIE, Balanagar, Hyderabad, Telangana 500037. We're just 10 minutes from Kukatpally, KPHB Colony, and easily accessible from Miyapur, Kondapur, Gachibowli, and all major areas."
  },
  {
    q: "How do I book an appointment?",
    a: "You can book online through our website by clicking 'Book for Ultimate Shine', call us directly at +91 99564 56499, or send a WhatsApp message. We also accept walk-ins, but appointments are recommended for quicker service."
  },
  {
    q: "Do you offer 4x4 accessories and modifications?",
    a: "Yes, we provide premium 4x4 accessories and off-road modifications including bull bars, roof racks, side steps, LED lighting, and performance upgrades for SUVs and off-road vehicles."
  },
  {
    q: "What brands of cars do you service?",
    a: "We service all car brands — from luxury vehicles like BMW, Mercedes, Audi, Porsche, Range Rover to popular models like Hyundai, Kia, Tata, Maruti, Toyota, Honda, and more. Every car gets the same premium treatment."
  }
];

function FAQSection() {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <section style={{ padding: "90px clamp(16px, 5vw, 80px)", background: "var(--bg-0)" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <Reveal direction="left"><SectionTag tag="FAQ" title="Frequently Asked Questions" sub="Everything you need to know about our services." /></Reveal>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
          {FAQ_DATA.map((faq, i) => (
            <Reveal key={i} delay={i * 0.05} direction="zoom-up">
              <div style={{
                background: "var(--bg-2)", border: "1px solid var(--card-border)", borderRadius: 12,
                overflow: "hidden", transition: "border-color 0.3s",
                borderColor: openIdx === i ? "rgba(230,57,70,0.3)" : "var(--card-border)"
              }}>
                <button onClick={() => setOpenIdx(openIdx === i ? null : i)} style={{
                  width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "20px 24px", background: "none", border: "none", cursor: "pointer",
                  textAlign: "left", gap: 16
                }}>
                  <span style={{
                    fontFamily: "'Oswald', sans-serif", fontSize: "0.88rem",
                    fontWeight: 600, color: openIdx === i ? RED : "var(--text)", transition: "color 0.3s",
                    lineHeight: 1.4
                  }}>{faq.q}</span>
                  <span style={{
                    width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                    background: openIdx === i ? "rgba(230,57,70,0.1)" : "var(--input-bg)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.3s", transform: openIdx === i ? "rotate(45deg)" : "rotate(0)"
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={openIdx === i ? RED : "var(--text-faded)"} strokeWidth="2" strokeLinecap="round">
                      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </span>
                </button>
                <div style={{
                  maxHeight: openIdx === i ? 300 : 0, overflow: "hidden",
                  transition: "max-height 0.4s ease, padding 0.4s ease",
                  padding: openIdx === i ? "0 24px 20px" : "0 24px 0"
                }}>
                  <p style={{
                    fontFamily: "'Poppins', sans-serif", fontSize: "0.88rem", color: "var(--text-muted)",
                    lineHeight: 1.75
                  }}>{faq.a}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FOOTER ───
function Footer({ navigate }) {
  return (
    <footer style={{ background: "var(--bg-0)", borderTop: "1px solid var(--card-border)", padding: "70px clamp(16px, 5vw, 80px) 28px" }}>
      <div style={{ maxWidth: 1400, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 44 }}>
        <div>
          <div style={{ marginBottom: 18 }}>
            <img src="./assets/logos/onyxcermaic_logo.svg" alt="Onyx Ceramic Car Care" style={{ height: 40, objectFit: "contain" }} />
          </div>
          <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: "0.92rem", color: "var(--footer-text)", lineHeight: 1.7, maxWidth: 290 }}>Reviving your vehicle's brilliance through meticulous care and expertise. Trust us to bring out the best in your car.</p>
          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            {[["https://instagram.com/onyxceramiccarcare", Icons.instagram],["https://www.facebook.com/61560733641254", Icons.facebook],["https://www.youtube.com/@OnyxCeramicCarCare", Icons.youtube],["https://x.com/Onyxceramic", Icons.twitter],["https://www.reddit.com/user/onyxceramiccarcare/", Icons.reddit],["https://www.linkedin.com/company/onyxceramiccarcare/", Icons.linkedin]].map(([url, icon], i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{ width: 40, height: 40, borderRadius: 8, border: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--footer-text)", transition: "all 0.3s", textDecoration: "none" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = RED; e.currentTarget.style.color = RED; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; e.currentTarget.style.color = "var(--text-muted)"; }}
              >{icon(17)}</a>
            ))}
          </div>
        </div>
        <div>
          <h4 style={{ fontFamily: "'Poppins', sans-serif", fontSize: "1rem", color: RED, letterSpacing: "0.2em", marginBottom: 18 }}>QUICK LINKS</h4>
          {[["Home","home"],["About Us","about"],["Gallery","gallery"],["Blog","blog"],["Contact Us","contact"]].map(([l,p]) => (
            <button key={p} onClick={() => navigate(p)} style={{ display: "block", background: "none", border: "none", color: "var(--footer-text)", fontFamily: "'Poppins', sans-serif", fontSize: "0.92rem", fontWeight: 400, cursor: "pointer", padding: "6px 0", transition: "color 0.3s" }}
            onMouseEnter={e => e.target.style.color = "var(--text)"} onMouseLeave={e => e.target.style.color = "var(--footer-text)"}>{l}</button>
          ))}
        </div>
        <div>
          <h4 style={{ fontFamily: "'Poppins', sans-serif", fontSize: "1rem", color: RED, letterSpacing: "0.2em", marginBottom: 18 }}>OUR SERVICES</h4>
          {SERVICES_LIST.map(s => (
            <button key={s.id} onClick={() => navigate(s.id)} style={{ display: "block", background: "none", border: "none", color: "var(--footer-text)", fontFamily: "'Poppins', sans-serif", fontSize: "0.92rem", fontWeight: 400, cursor: "pointer", padding: "6px 0", transition: "color 0.3s" }}
            onMouseEnter={e => e.target.style.color = "var(--text)"} onMouseLeave={e => e.target.style.color = "var(--footer-text)"}>{s.title}</button>
          ))}
        </div>
        <div>
          <h4 style={{ fontFamily: "'Poppins', sans-serif", fontSize: "1rem", color: RED, letterSpacing: "0.2em", marginBottom: 18 }}>CONTACT INFO</h4>
          {[[Icons.mapPin, "IDA, 5/A Opposite IDPL Research Centre, SVCIE, Balanagar, Hyderabad, Telangana 500037", "https://www.google.com/maps/place/Onyx+Ceramic+Car+Care/data=!4m2!3m1!1s0x0:0xc7cf61d782721d3"],[Icons.phone, "+91 99564 56499", "tel:+919956456499"],[Icons.mail, "info@onyxceramiccarcare.com", "mailto:info@onyxceramiccarcare.com"]].map(([icon, text, href], i) => (
            <a key={i} href={href} target={i === 0 ? "_blank" : undefined} rel={i === 0 ? "noopener noreferrer" : undefined} style={{ display: "flex", alignItems: "flex-start", gap: 10, color: "var(--footer-text)", fontSize: "0.92rem", fontFamily: "'Poppins', sans-serif", marginBottom: 12, textDecoration: "none", transition: "color 0.3s" }}
            onMouseEnter={e => e.currentTarget.style.color = "#fff"}
            onMouseLeave={e => e.currentTarget.style.color = "var(--footer-text)"}>
              <span style={{ flexShrink: 0, marginTop: 3 }}>{icon()}</span><span>{text}</span>
            </a>
          ))}
        </div>
      </div>
      {/* Areas Served — Hidden visually, crawlable for SEO */}
      <div style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap" }} aria-hidden="false">
        <p>AREAS WE SERVE</p>
        <p>
          Balanagar • Kukatpally • KPHB Colony • Miyapur • Chandanagar • Lingampally • Kondapur • Gachibowli • Madhapur • HITEC City • Jubilee Hills • Banjara Hills • Film Nagar • Ameerpet • Begumpet • Somajiguda • Punjagutta • Secunderabad • Kompally • Alwal • Bowenpally • Malkajgiri • Tarnaka • Uppal • Dilsukhnagar • LB Nagar • Mehdipatnam • Tolichowki • Manikonda • Narsingi • Kokapet • Bachupally • Nizampet • Pragathi Nagar • Patancheru • Medchal
        </p>
        <p>
          Best Ceramic Coating in Hyderabad | PPF Installation Near Kukatpally | Car Detailing Balanagar | Graphene Coating KPHB | Car Wash Miyapur | Interior Detailing Kondapur | Paint Correction Gachibowli | Sun Film Hyderabad | Sun Film Near Me | 4x4 Accessories Hyderabad | Car Wash Near Me | Ceramic Coating Near Me | PPF Near Me | Detailing Studio Near Me | Interior Cleaning Near Me | Car Spa Hyderabad | Best Detailing Studio Balanagar | Sun Film Balanagar | Sun Film Kukatpally | 4x4 Accessories Near Me | onyxindia4x4.com — Premium 4x4 Accessories Hyderabad
        </p>
      </div>

      <div style={{ maxWidth: 1400, margin: "44px auto 0", paddingTop: 22, borderTop: "1px solid var(--divider)", textAlign: "center", color: "var(--text-faded)", fontFamily: "'Poppins', sans-serif", fontSize: "0.82rem", letterSpacing: "0.08em" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 12 }}>
          <span>Our Sister Brand:</span>
          <a href="https://onyxindia4x4.com" target="_blank" rel="noopener noreferrer" style={{ color: RED, fontWeight: 600, textDecoration: "none", transition: "opacity 0.3s" }}
          onMouseEnter={e => e.currentTarget.style.opacity = "0.8"}
          onMouseLeave={e => e.currentTarget.style.opacity = "1"}>onyxindia4x4.com — 4x4 Accessories & Off-Road Mods</a>
        </div>
        © 2026 ONYX CERAMIC CAR CARE. All Rights Reserved.
      </div>
    </footer>
  );
}

// ─── WHATSAPP FAB ───
function WhatsAppFAB() {
  return (
    <a href="https://wa.me/919956456499" target="_blank" rel="noopener noreferrer" style={{
      position: "fixed", bottom: 24, right: 24, width: 54, height: 54, borderRadius: "50%",
      background: "#25D366", display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: "0 4px 20px rgba(37,211,102,0.4)", zIndex: 999, transition: "transform 0.3s",
      textDecoration: "none", animation: "waBounce 4s ease infinite"
    }}
    onMouseEnter={e => e.currentTarget.style.transform = "scale(1.15)"}
    onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
    >{Icons.whatsapp(26)}</a>
  );
}

// ─── SEO HELPER ───
const SEO_DATA = {
  home: {
    title: "Onyx Ceramic Car Care | Best Ceramic Coating, PPF, Sun Film & Car Detailing Near Me | Hyderabad",
    desc: "Hyderabad's #1 premium car detailing studio in Balanagar. Expert ceramic coating, PPF, graphene coating, sun film, car wash, interior cleaning & 4x4 accessories. Best car detailing near me in Kukatpally, KPHB, Miyapur, Kondapur & all Hyderabad. Call +91 99564 56499."
  },
  ppf: {
    title: "Best PPF (Paint Protection Film) in Hyderabad | Balanagar, Kukatpally | Onyx Ceramic",
    desc: "Premium self-healing PPF installation in Hyderabad. Protect your car from scratches, rock chips & UV damage. 10-year warranty. Best PPF near Kukatpally, KPHB, Miyapur, Balanagar. Call +91 99564 56499."
  },
  "ceramic-coating": {
    title: "Best Ceramic Coating in Hyderabad | 9H 10H Coating | Balanagar, Kukatpally | Onyx Ceramic",
    desc: "Professional 9H & 10H ceramic coating in Hyderabad. Mirror-like gloss, hydrophobic protection, UV resistance. Best ceramic coating in Balanagar, Kukatpally, KPHB, Miyapur. Book now: +91 99564 56499."
  },
  "car-wash": {
    title: "Premium Car Wash in Hyderabad | Foam Wash & Car Spa | Balanagar | Onyx Ceramic",
    desc: "Premium pH-balanced foam car wash in Hyderabad. Two-bucket method, iron decontamination, clay bar treatment, purified water rinse. Best car wash near Kukatpally, Balanagar, KPHB."
  },
  interior: {
    title: "Best Interior Car Detailing in Hyderabad | Deep Cleaning | Balanagar | Onyx Ceramic",
    desc: "Professional interior car detailing in Hyderabad. Steam cleaning, leather conditioning, dashboard restoration, odor elimination. Serving Balanagar, Kukatpally, KPHB, Miyapur."
  },
  "paint-correction": {
    title: "Paint Correction & Polishing in Hyderabad | Scratch Removal | Onyx Ceramic",
    desc: "Multi-stage paint correction in Hyderabad. Remove swirl marks, scratches, oxidation. Restore factory-perfect finish. Best paint correction near Kukatpally, Balanagar."
  },
  graphene: {
    title: "Graphene Coating in Hyderabad | Next-Gen Protection | Balanagar | Onyx Ceramic",
    desc: "Advanced graphene coating in Hyderabad. Superior heat dissipation, reduced water spotting, enhanced durability. Best graphene coating in Balanagar, Kukatpally."
  },
  "sun-film": {
    title: "Best Sun Film & Window Tinting in Hyderabad | UV Protection | Balanagar | Onyx Ceramic",
    desc: "Premium sun film installation in Hyderabad. UV protection, heat rejection, glare reduction, enhanced privacy. Best sun film near Kukatpally, KPHB, Balanagar. Call +91 99564 56499."
  },
  "4x4-accessories": {
    title: "4x4 Accessories & Off-Road Mods in Hyderabad | Bull Bars, Roof Racks | Onyx Ceramic",
    desc: "Premium 4x4 accessories in Hyderabad. Bull bars, roof racks, LED lights, side steps, performance upgrades for SUVs. Best 4x4 accessories near Balanagar, Kukatpally."
  },
  gallery: {
    title: "Gallery | Car Detailing Work | Ceramic Coating & PPF Results | Onyx Ceramic Hyderabad",
    desc: "See our car detailing gallery — ceramic coating, PPF installation, paint correction results on luxury cars in Hyderabad. Onyx Ceramic Car Care, Balanagar."
  },
  about: {
    title: "About Us | Onyx Ceramic Car Care | Best Car Detailing Studio in Hyderabad",
    desc: "Learn about Onyx Ceramic Car Care — Hyderabad's premier car detailing studio in Balanagar. Certified experts, dust-free studio, premium products, 500+ cars detailed."
  },
  blog: {
    title: "Blog | Car Detailing Tips & Guides | Ceramic Coating, PPF, Sun Film | Onyx Ceramic Hyderabad",
    desc: "Expert car care blog from Onyx Ceramic Car Care. Read guides on ceramic coating, PPF, sun film, interior detailing, paint correction & more. Best detailing tips for Hyderabad car owners."
  },
  contact: {
    title: "Contact Us | Onyx Ceramic Car Care | Balanagar, Hyderabad | Call +91 99564 56499",
    desc: "Contact Onyx Ceramic Car Care for ceramic coating, PPF, car detailing in Hyderabad. Located in Balanagar. Call +91 99564 56499 or email info@onyxceramiccarcare.com."
  }
};

function SEOHead({ page }) {
  useEffect(() => {
    const seo = SEO_DATA[page] || SEO_DATA.home;
    document.title = seo.title;
    
    // Update meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", seo.desc);
    
    // Update OG tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", seo.title);
    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", seo.desc);
    
    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `https://onyxceramiccarcare.com/${page === "home" ? "" : page}`);
  }, [page]);
  
  return null;
}

// ─── HIDDEN SEO CONTENT (Semantic, accessible, crawlable) ───
function SEOContent() {
  return (
    <div style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap" }} role="complementary" aria-label="Additional information">
      <h2>Car Detailing Services in Hyderabad — Areas We Serve</h2>
      <p>Onyx Ceramic Car Care provides premium car detailing services across Hyderabad. Our studio in Balanagar serves customers from Kukatpally, KPHB Colony, Miyapur, Chandanagar, Lingampally, Kondapur, Gachibowli, Madhapur, HITEC City, Jubilee Hills, Banjara Hills, Film Nagar, Yousufguda, Ameerpet, Begumpet, Somajiguda, Punjagutta, Secunderabad, Kompally, Alwal, Bowenpally, Malkajgiri, Tarnaka, Habsiguda, Uppal, Dilsukhnagar, LB Nagar, Mehdipatnam, Tolichowki, Manikonda, Narsingi, Kokapet, Bachupally, Nizampet, Pragathi Nagar, Patancheru, Sangareddy, Shamirpet, Medchal, and surrounding areas in Telangana.</p>
      
      <h2>Best Ceramic Coating Near Me in Hyderabad</h2>
      <p>Looking for the best ceramic coating near Kukatpally? Onyx Ceramic Car Care offers professional 9H and 10H ceramic coatings in Balanagar, just minutes from Kukatpally, KPHB, and Miyapur. Our ceramic coating provides mirror-like gloss, hydrophobic water-beading, UV protection, and chemical resistance lasting 3-5 years.</p>
      
      <h2>PPF Installation Near Kukatpally, Balanagar</h2>
      <p>Onyx Ceramic Car Care is the best PPF installation center near Kukatpally and Balanagar. We use premium self-healing paint protection film with 10-year manufacturer warranty. Our dust-free studio ensures flawless bubble-free installation on every vehicle.</p>
      
      <h2>Car Detailing Studio Near KPHB, Miyapur</h2>
      <p>If you're searching for car detailing near KPHB Colony or Miyapur, Onyx Ceramic Car Care in Balanagar is your nearest premium option. We offer ceramic coating, PPF, graphene coating, premium car wash, interior detailing, and paint correction services.</p>
      
      <h2>Luxury Car Care in Hyderabad</h2>
      <p>From BMW and Mercedes to Audi, Porsche, Land Rover, and everyday vehicles — Onyx Ceramic Car Care has detailed 500+ cars with 100% customer satisfaction. We are Hyderabad's trusted choice for ceramic coating, PPF coating, graphene coating, and premium car detailing.</p>
      
      <h2>Best Car Detailing Near Me — Hyderabad</h2>
      <p>Searching for car detailing near me, ceramic coating near me, PPF near me, or car wash near me in Hyderabad? Onyx Ceramic Car Care in Balanagar is your nearest premium detailing studio. Just minutes from Kukatpally, KPHB Colony, Miyapur, and all major Hyderabad areas.</p>
      
      <h2>Sun Film Installation in Hyderabad</h2>
      <p>Onyx Ceramic Car Care offers premium sun film and window tinting services in Hyderabad. Our sun control films provide excellent UV protection, superior heat rejection, glare reduction, and enhanced privacy. We install sun film for all car brands including SUVs, sedans, and hatchbacks. Best sun film near Balanagar, Kukatpally, KPHB, Miyapur.</p>
      
      <h2>4x4 Accessories in Hyderabad</h2>
      <p>Looking for 4x4 accessories near me in Hyderabad? Onyx Ceramic Car Care provides premium 4x4 and off-road accessories including bull bars, roof racks, side steps, LED lighting, snorkels, and performance upgrades for SUVs and off-road vehicles. Best 4x4 accessories shop near Balanagar, Kukatpally, and Miyapur.</p>
      
      <h2>Interior Cleaning Near Me — Hyderabad</h2>
      <p>Professional interior car cleaning near Balanagar, Kukatpally, KPHB. Deep steam cleaning, leather conditioning, dashboard restoration, carpet shampooing, and odor elimination. Best interior cleaning service in Hyderabad.</p>
      
      <h2>Onyx India 4x4 — Our Sister Brand</h2>
      <p>Onyx India 4x4 (onyxindia4x4.com) is the sister brand of Onyx Ceramic Car Care, specializing in premium 4x4 accessories, off-road modifications, bull bars, roof racks, LED light bars, side steps, snorkels, and performance upgrades for SUVs and off-road vehicles in Hyderabad. Visit onyxindia4x4.com for the best 4x4 accessories near Balanagar, Kukatpally, KPHB, and all Hyderabad areas.</p>
      
      <address>
        Onyx Ceramic Car Care,
        IDA, 5/A Opposite IDPL Research Centre, SVCIE,
        Balanagar, Hyderabad, Telangana 500037, India.
        Phone: +91 99564 56499.
        Email: info@onyxceramiccarcare.com
      </address>
    </div>
  );
}

// ─── LOADING SCREEN ───
function LoadingScreen({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(0); // 0=loading, 1=complete, 2=exit
  
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          clearInterval(timer);
          setTimeout(() => setPhase(1), 300);
          setTimeout(() => setPhase(2), 1400);
          setTimeout(() => onComplete(), 2000);
          return 100;
        }
        const increment = p < 30 ? 4 : p < 70 ? 2 : p < 90 ? 3 : 5;
        return Math.min(p + increment, 100);
      });
    }, 40);
    return () => clearInterval(timer);
  }, []);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 99999,
      background: "#0b0b0b",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      opacity: phase === 2 ? 0 : 1,
      transform: phase === 2 ? "scale(1.05)" : "scale(1)",
      transition: "opacity 0.6s ease, transform 0.6s ease",
      pointerEvents: phase === 2 ? "none" : "auto"
    }}>
      {/* Ambient red glow */}
      <div style={{
        position: "absolute", width: 400, height: 400,
        background: "radial-gradient(circle, rgba(230,57,70,0.06) 0%, transparent 70%)",
        borderRadius: "50%", pointerEvents: "none",
        animation: "loaderPulse 3s ease-in-out infinite"
      }} />

      {/* Full Logo SVG — ONYX CERAMIC CAR CARE */}
      <div style={{
        position: "relative", zIndex: 2, width: "clamp(260px, 50vw, 420px)",
        transform: phase === 1 ? "scale(1.08)" : "scale(1)",
        transition: "transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.6s ease",
        filter: phase === 1 ? "drop-shadow(0 0 40px rgba(230,57,70,0.35))" : "drop-shadow(0 0 20px rgba(230,57,70,0.1))",
      }}>
        <svg viewBox="0 0 936.54 197.29" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "auto" }}>
          {/* Hexagon icon — red, draws in first */}
          <g style={{ opacity: progress > 5 ? 1 : 0, transition: "opacity 0.6s ease" }}>
            <polygon fill="#E63946" points="74.91 170.87 33.3 98.01 74.8 26.09 185.68 26.09 170.59 0 56.69 0 16.79 69.11 0 98.21 16.52 127.1 56.69 197.29 170.59 197.29 185.83 170.87 74.91 170.87"
              style={{ animation: "loaderIconPulse 2.5s ease-in-out infinite" }} />
            <path fill="#E63946" d="m226.07,98.63l-8.61-14.9h.05l-10.32-17.85-.77-1.26-5.94-10.27h-109.4l-5.94,10.27-.77,1.26-10.32,17.85h.05l-8.61,14.9,8.89,15.4h-.04l16.96,29.38h108.97l16.96-29.38h-.04l8.89-15.4Zm-118.62,15.4l-8.58-15.17,8.58-15.14h76.65l8.58,15.14-8.58,15.17h-76.65Z"
              style={{ animation: "loaderIconPulse 2.5s ease-in-out 0.2s infinite" }} />
          </g>

          {/* ONYX text — white, fades in after icon */}
          <g style={{ opacity: progress > 20 ? 1 : 0, transform: progress > 20 ? "translateX(0)" : "translateX(-10px)", transition: "opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s" }}>
            <path fill="#ffffff" d="m368.29,13.11c36.12,0,57.1,19.93,57.1,52.6v.15c0,32.82-20.98,52.75-57.1,52.75h-45.11c-36.12,0-57.1-19.93-57.1-52.75v-.15c0-32.67,20.98-52.6,57.1-52.6h45.11Zm0,84.07c22.33,0,33.72-11.84,33.72-31.32v-.15c0-19.33-11.39-31.17-33.72-31.17h-45.11c-22.33,0-33.72,11.84-33.72,31.17v.15c0,19.48,11.39,31.32,33.72,31.32h45.11Z"/>
            <path fill="#ffffff" d="m575.09,14.01h23.68v103.7h-22.33l-93.21-71.63v71.63h-23.53V14.01h22.18l93.21,71.48V14.01Z"/>
            <path fill="#ffffff" d="m701.12,62.71l44.96-48.7h29.97l-62.94,67.73v35.97h-23.83v-35.97l-62.94-67.73h29.97l44.81,48.7Z"/>
            <path fill="#ffffff" d="m883.05,66.01l53.5,51.7h-31.62l-40.01-39.41-39.86,39.41h-31.62l53.35-51.7-52.75-52h31.62l39.26,39.26,39.41-39.26h31.47l-52.75,52Z"/>
          </g>

          {/* CERAMIC CAR CARE text — white, fades in last */}
          <g style={{ opacity: progress > 40 ? 1 : 0, transition: "opacity 1s ease 0.4s" }}>
            <path fill="rgba(255,255,255,0.7)" d="m302.4,161.79c-1.92-1.88-4.57-2.39-8.12-2.39h-11.84c-6.15,0-9.62,3.33-9.62,8.89v.04c.04,5.64,3.5,8.93,9.62,8.93h11.84c3.68,0,6.24-.6,8.12-2.44l3.93,4.83c-2.91,2.69-6.54,3.72-12.05,3.72h-11.84c-10.3,0-16.28-5.68-16.28-15.04v-.04c0-9.32,5.98-15,16.28-15h11.84c5.34,0,9.1.94,12.05,3.72l-3.93,4.79Z"/>
            <path fill="rgba(255,255,255,0.7)" d="m349.24,153.54v5.86h-27.95v6.11h25.3v5.73h-25.3v5.98h27.95v5.9h-34.58v-29.58h34.58Z"/>
            <path fill="rgba(255,255,255,0.7)" d="m397.27,183.12h-7.48l-5.98-9.15h-18.51v9.15h-6.62v-29.58h25.09c9.15,0,12.78,4.32,12.78,10.3v.04c0,4.23-1.84,7.57-6.28,9.15l7.01,10.09Zm-31.97-23.63v8.8h18.46c4.19,0,6.11-1.41,6.11-4.4v-.04c0-2.95-1.92-4.36-6.11-4.36h-18.46Z"/>
            <path fill="rgba(255,255,255,0.7)" d="m448.04,183.12h-7.35l-3.72-6.03h-22.57l-3.72,6.03h-7.35l18.98-29.58h6.75l18.98,29.58Zm-22.35-23.68l-7.86,12.1h15.69l-7.82-12.1Z"/>
            <path fill="rgba(255,255,255,0.7)" d="m478.72,170.51l16.2-16.97h6.45v29.58h-6.75v-20.17l-14.27,15.04h-3.25l-14.28-15.04v20.17h-6.71v-29.58h6.41l16.2,16.97Z"/>
            <path fill="rgba(255,255,255,0.7)" d="m512,183.12v-29.58h6.8v29.58h-6.8Z"/>
            <path fill="rgba(255,255,255,0.7)" d="m563.97,161.79c-1.92-1.88-4.57-2.39-8.12-2.39h-11.84c-6.15,0-9.62,3.33-9.62,8.89v.04c.04,5.64,3.5,8.93,9.62,8.93h11.84c3.68,0,6.24-.6,8.12-2.44l3.93,4.83c-2.91,2.69-6.54,3.72-12.05,3.72h-11.84c-10.3,0-16.28-5.68-16.28-15.04v-.04c0-9.32,5.98-15,16.28-15h11.84c5.34,0,9.1.94,12.05,3.72l-3.93,4.79Z"/>
            <path fill="rgba(255,255,255,0.7)" d="m629.05,161.79c-1.92-1.88-4.57-2.39-8.12-2.39h-11.84c-6.15,0-9.62,3.33-9.62,8.89v.04c.04,5.64,3.5,8.93,9.62,8.93h11.84c3.68,0,6.24-.6,8.12-2.44l3.93,4.83c-2.91,2.69-6.54,3.72-12.05,3.72h-11.84c-10.3,0-16.28-5.68-16.28-15.04v-.04c0-9.32,5.98-15,16.28-15h11.84c5.34,0,9.1.94,12.05,3.72l-3.93,4.79Z"/>
            <path fill="rgba(255,255,255,0.7)" d="m682.13,183.12h-7.35l-3.72-6.03h-22.57l-3.72,6.03h-7.35l18.98-29.58h6.75l18.98,29.58Zm-22.35-23.68l-7.86,12.1h15.69l-7.82-12.1Z"/>
            <path fill="rgba(255,255,255,0.7)" d="m728.79,183.12h-7.48l-5.98-9.15h-18.51v9.15h-6.62v-29.58h25.09c9.15,0,12.78,4.32,12.78,10.3v.04c0,4.23-1.84,7.57-6.28,9.15l7.01,10.09Zm-31.97-23.63v8.8h18.46c4.19,0,6.11-1.41,6.11-4.4v-.04c0-2.95-1.92-4.36-6.11-4.36h-18.46Z"/>
            <path fill="rgba(255,255,255,0.7)" d="m790.33,161.79c-1.92-1.88-4.57-2.39-8.12-2.39h-11.84c-6.15,0-9.62,3.33-9.62,8.89v.04c.04,5.64,3.5,8.93,9.62,8.93h11.84c3.68,0,6.24-.6,8.12-2.44l3.93,4.83c-2.91,2.69-6.54,3.72-12.05,3.72h-11.84c-10.3,0-16.28-5.68-16.28-15.04v-.04c0-9.32,5.98-15,16.28-15h11.84c5.34,0,9.1.94,12.05,3.72l-3.93,4.79Z"/>
            <path fill="rgba(255,255,255,0.7)" d="m843.41,183.12h-7.35l-3.72-6.03h-22.57l-3.72,6.03h-7.35l18.98-29.58h6.75l18.98,29.58Zm-22.35-23.68l-7.86,12.1h15.69l-7.82-12.1Z"/>
            <path fill="rgba(255,255,255,0.7)" d="m890.07,183.12h-7.48l-5.98-9.15h-18.51v9.15h-6.62v-29.58h25.09c9.15,0,12.78,4.32,12.78,10.3v.04c0,4.23-1.84,7.57-6.28,9.15l7.01,10.09Zm-31.97-23.63v8.8h18.46c4.19,0,6.11-1.41,6.11-4.4v-.04c0-2.95-1.92-4.36-6.11-4.36h-18.46Z"/>
            <path fill="rgba(255,255,255,0.7)" d="m933.36,153.54v5.86h-27.95v6.11h25.3v5.73h-25.3v5.98h27.95v5.9h-34.58v-29.58h34.58Z"/>
          </g>
        </svg>
      </div>

      {/* Red line separator — animates width */}
      <div style={{
        width: progress > 30 ? 120 : 0, height: 2, marginTop: 32,
        background: "linear-gradient(90deg, transparent, #E63946, transparent)",
        transition: "width 0.8s ease", borderRadius: 1, position: "relative", zIndex: 2
      }} />

      {/* Progress bar */}
      <div style={{
        position: "absolute", bottom: 70, left: "50%", transform: "translateX(-50%)",
        width: 180, display: "flex", flexDirection: "column", alignItems: "center", gap: 10
      }}>
        <div style={{ width: "100%", height: 1.5, background: "rgba(255,255,255,0.05)", borderRadius: 1, overflow: "hidden" }}>
          <div style={{
            width: `${progress}%`, height: "100%",
            background: "linear-gradient(90deg, #E63946, #ff6b6b)",
            borderRadius: 1, transition: "width 0.15s ease",
            boxShadow: "0 0 8px rgba(230,57,70,0.3)"
          }} />
        </div>
      </div>

      {/* Bottom tagline */}
      <p style={{
        position: "absolute", bottom: 28,
        fontFamily: "'Poppins', sans-serif", fontSize: "0.58rem", fontWeight: 400,
        color: "rgba(255,255,255,0.1)", letterSpacing: "0.25em", textTransform: "uppercase"
      }}>Hyderabad's Premier Detailing Studio</p>
    </div>
  );
}

// ═══ MAIN APP ═══
// ─── THEME TOGGLE BUTTON ───
function ThemeToggle({ theme, setTheme }) {
  const isDark = theme === "dark";
  return (
    <button onClick={() => setTheme(isDark ? "light" : "dark")} title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"} style={{
      width: 40, height: 40, borderRadius: 10, border: "1px solid var(--card-border)",
      background: "var(--card)", display: "flex", alignItems: "center", justifyContent: "center",
      cursor: "pointer", transition: "all 0.3s", position: "relative", overflow: "hidden", flexShrink: 0
    }}
    onMouseEnter={e => e.currentTarget.style.borderColor = "var(--red)"}
    onMouseLeave={e => e.currentTarget.style.borderColor = "var(--card-border)"}>
      {/* Sun */}
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={isDark ? "#777" : "#f5a623"} strokeWidth="2" strokeLinecap="round"
        style={{ position: "absolute", transition: "all 0.4s cubic-bezier(0.34,1.56,0.64,1)", opacity: isDark ? 0 : 1, transform: isDark ? "rotate(-90deg) scale(0.5)" : "rotate(0) scale(1)" }}>
        <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
        <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
      </svg>
      {/* Moon */}
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={isDark ? "#e0e0e0" : "#777"} strokeWidth="2" strokeLinecap="round"
        style={{ position: "absolute", transition: "all 0.4s cubic-bezier(0.34,1.56,0.64,1)", opacity: isDark ? 1 : 0, transform: isDark ? "rotate(0) scale(1)" : "rotate(90deg) scale(0.5)" }}>
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
      </svg>
    </button>
  );
}

export default function App() {
  const [page, setPage] = useState("home");
  const [bookOpen, setBookOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("onyx-theme");
      if (saved) return saved;
      return "dark";
    }
    return "dark";
  });

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("onyx-theme", theme);
  }, [theme]);

  const navigate = useCallback((p) => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }, []);

  const t = THEMES[theme]; // current theme object
  const DARK = t.bg0;
  const DARK2 = t.bg1;
  const DARK3 = t.bg2;
  const GREY = t.textMuted;
  const LIGHT_GREY = t.textSoft;

  const pages = {
    home: <HomePage navigate={navigate} onBook={() => setBookOpen(true)} />,
    ppf: <PPFPage />, "ceramic-coating": <CeramicPage />, "car-wash": <CarWashPage />,
    interior: <InteriorPage />, "paint-correction": <PaintCorrectionPage />, graphene: <GraphenePage />,
    "sun-film": <SunFilmPage />, "4x4-accessories": <FourByFourPage />, blog: <BlogPage />,
    gallery: <GalleryPage />, about: <AboutPage />, contact: <ContactPage />,
  };

  return (
    <ThemeContext.Provider value={t}>
    <div data-theme={theme} style={{ background: "var(--bg-0)", minHeight: "100vh", color: "var(--text)", transition: "background 0.4s ease, color 0.4s ease" }}>
      {loading && <LoadingScreen onComplete={() => setLoading(false)} />}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Poppins:wght@300;400;500;600;700&display=swap');
        /* Oswald loaded via Google Fonts import above */
        * { margin: 0; padding: 0; box-sizing: border-box; }
        
        /* Theme CSS Variables */
        :root, [data-theme="dark"] {
          --bg-0: #0b0b0b; --bg-1: #111111; --bg-2: #161616; --bg-3: #1c1c1c;
          --red: #E63946; --red-dark: #c0392b;
          --text: #ffffff; --text-soft: #e0e0e0; --text-muted: #cccccc;
          --text-faded: #888888; --text-dim: #555555;
          --card: #161616; --card-border: rgba(255,255,255,0.06);
          --input-bg: rgba(255,255,255,0.04); --input-border: rgba(255,255,255,0.2);
          --header-bg: rgba(11,11,11,0.96); --header-border: rgba(230,57,70,0.12);
          --nav-link: #cccccc; --footer-text: #bbbbbb;
          --divider: rgba(255,255,255,0.04); --overlay: rgba(0,0,0,0.75);
          --select-bg: #111111; --scroll-thumb: rgba(230,57,70,0.3);
          --shadow-card: 0 20px 50px rgba(0,0,0,0.35);
          --map-filter: grayscale(1) invert(0.92) contrast(1.2);
          --logo-filter: none;
          color-scheme: dark;
        }
        [data-theme="light"] {
          --bg-0: #f5f3f0; --bg-1: #edeae6; --bg-2: #ffffff; --bg-3: #f9f7f5;
          --red: #D62839; --red-dark: #b71c2a;
          --text: #1a1a1a; --text-soft: #2d2d2d; --text-muted: #4a4a4a;
          --text-faded: #777777; --text-dim: #999999;
          --card: #ffffff; --card-border: rgba(0,0,0,0.08);
          --input-bg: #ffffff; --input-border: rgba(0,0,0,0.12);
          --header-bg: rgba(255,255,255,0.97); --header-border: rgba(0,0,0,0.06);
          --nav-link: #333333; --footer-text: #555555;
          --divider: rgba(0,0,0,0.06); --overlay: rgba(0,0,0,0.5);
          --select-bg: #ffffff; --scroll-thumb: rgba(214,40,57,0.35);
          --shadow-card: 0 10px 40px rgba(0,0,0,0.08);
          --map-filter: none;
          --logo-filter: none;
          color-scheme: light;
        }
        [data-theme="light"] body, [data-theme="light"] { background: var(--bg-0); color: var(--text); }
        [data-theme="light"] .hero-car-wrap { opacity: 0.3 !important; }
        [data-theme="light"] .hero-car-wrap img, [data-theme="light"] .hero-car-wrap video { filter: brightness(1.2) contrast(0.8) saturate(0.35); }
        [data-theme="light"] .hero-left-fade { background: linear-gradient(to right, #f5f3f0 40%, rgba(245,243,240,0.7) 60%, transparent 80%) !important; }
        [data-theme="light"] .hero-bottom-fade { background: linear-gradient(to top, #f5f3f0, transparent) !important; }
        [data-theme="light"] .hero-top-fade { background: linear-gradient(to bottom, #f5f3f0, transparent) !important; }
        body { overflow-x: hidden; }
        ::selection { background: rgba(230,57,70,0.3); color: #fff; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: var(--bg-0); }
        ::-webkit-scrollbar-thumb { background: var(--scroll-thumb); border-radius: 3px; }
        
        /* Loading screen logo animations */
        @keyframes loaderPulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }
        @keyframes loaderIconPulse {
          0%, 100% { filter: drop-shadow(0 0 0 rgba(230,57,70,0)); }
          50% { filter: drop-shadow(0 0 12px rgba(230,57,70,0.4)); }
        }
        @keyframes logoPulseOuter {
          0%, 100% { opacity: 0.8; filter: drop-shadow(0 0 0px rgba(230,57,70,0)); }
          50% { opacity: 1; filter: drop-shadow(0 0 8px rgba(230,57,70,0.3)); }
        }
        @keyframes logoPulseInner {
          0%, 100% { opacity: 0.85; filter: drop-shadow(0 0 0px rgba(230,57,70,0)); }
          50% { opacity: 1; filter: drop-shadow(0 0 12px rgba(230,57,70,0.4)); }
        }
        
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes scrollLine {
          0% { transform: scaleY(0); transform-origin: top; }
          50% { transform: scaleY(1); transform-origin: top; }
          50.01% { transform-origin: bottom; }
          100% { transform: scaleY(0); transform-origin: bottom; }
        }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes heroWordReveal {
          0% { opacity: 0; transform: translateY(40px) rotateX(-90deg); filter: blur(10px); }
          60% { filter: blur(0); }
          100% { opacity: 1; transform: translateY(0) rotateX(0); filter: blur(0); }
        }
        @keyframes heroGlow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        .hero-word {
          display: inline-block;
          opacity: 0;
          animation: heroWordReveal 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          transform-origin: center bottom;
          perspective: 1000px;
        }
        @keyframes popIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        
        /* Floating animation */
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        
        /* Shimmer effect for cards */
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        
        /* Glow pulse for buttons */
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 4px 20px rgba(230,57,70,0.3); }
          50% { box-shadow: 0 6px 35px rgba(230,57,70,0.5); }
        }
        
        /* Border glow animation */
        @keyframes borderGlow {
          0%, 100% { border-color: rgba(230,57,70,0.1); }
          50% { border-color: rgba(230,57,70,0.3); }
        }
        
        /* Gradient background shift */
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        /* Scale bounce */
        @keyframes scaleBounce {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        
        /* Rotate subtle */
        @keyframes rotateSlow {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        /* WhatsApp bounce */
        @keyframes waBounce {
          0%, 100% { transform: scale(1); }
          15% { transform: scale(1.15); }
          30% { transform: scale(1); }
          45% { transform: scale(1.08); }
          60% { transform: scale(1); }
        }
        
        /* Underline expand */
        @keyframes expandWidth {
          from { width: 0; }
          to { width: 50px; }
        }
        
        /* Card hover glow */
        .card-glow { position: relative; overflow: hidden; }
        .card-glow::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(230,57,70,0.04) 0%, transparent 70%);
          opacity: 0;
          transition: opacity 0.5s ease;
          pointer-events: none;
          z-index: 0;
        }
        .card-glow:hover::before { opacity: 1; }
        
        /* Shimmer on hover for images */
        .img-shimmer { position: relative; overflow: hidden; }
        .img-shimmer::after {
          content: '';
          position: absolute;
          top: 0; left: -100%; width: 50%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent);
          transition: none;
        }
        .img-shimmer:hover::after {
          animation: shimmer 0.8s ease forwards;
          left: 100%;
        }
        
        /* Button shine effect */
        .btn-shine { position: relative; overflow: hidden; }
        .btn-shine::after {
          content: '';
          position: absolute;
          top: 0; left: -100%; width: 100%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
          transition: none;
        }
        .btn-shine:hover::after {
          left: 100%;
          transition: left 0.6s ease;
        }
        
        /* Smooth icon hover rotation */
        .icon-hover { transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
        .icon-hover:hover { transform: scale(1.15) rotate(5deg); }
        
        /* Text gradient animation */
        .text-gradient {
          background: linear-gradient(90deg, #E63946, #ff6b6b, #E63946);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: gradientShift 3s ease infinite;
        }
        
        /* Parallax scroll helper */
        .parallax-slow { will-change: transform; transition: transform 0.1s linear; }
        
        /* Link underline animation */
        .link-anim { position: relative; }
        .link-anim::after {
          content: '';
          position: absolute;
          bottom: -2px; left: 0;
          width: 0; height: 2px;
          background: #E63946;
          transition: width 0.3s ease;
        }
        .link-anim:hover::after { width: 100%; }
        
        /* Stagger fade children */
        .stagger-children > * {
          opacity: 0;
          animation: fadeIn 0.6s ease forwards;
        }
        .stagger-children > *:nth-child(1) { animation-delay: 0s; }
        .stagger-children > *:nth-child(2) { animation-delay: 0.1s; }
        .stagger-children > *:nth-child(3) { animation-delay: 0.2s; }
        .stagger-children > *:nth-child(4) { animation-delay: 0.25s; }
        .stagger-children > *:nth-child(5) { animation-delay: 0.3s; }
        .stagger-children > *:nth-child(6) { animation-delay: 0.35s; }
        /* Animated section divider */
        .section-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(230,57,70,0.3), transparent);
          background-size: 200% 100%;
          animation: gradientShift 4s ease infinite;
        }
        
        /* Smooth image zoom on card hover */
        .card-img-zoom img { transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1); }
        .card-img-zoom:hover img { transform: scale(1.08); }
        
        /* Red accent line that animates in */
        .accent-line {
          width: 0;
          height: 3px;
          background: #E63946;
          border-radius: 2px;
          animation: expandWidth 0.8s ease forwards;
        }
        
        /* Testimonial card subtle breathing */
        @keyframes breathe {
          0%, 100% { border-color: rgba(255,255,255,0.05); }
          50% { border-color: rgba(230,57,70,0.12); }
        }
        .testimonial-breathe { animation: breathe 4s ease infinite; }
        
        /* Service icon float on hover */
        .svc-card:hover .svc-icon { animation: float 2s ease-in-out infinite; }
        
        /* Smooth page content transition */
        .page-enter { animation: fadeIn 0.5s ease forwards; }

        /* ═══ LIGHT MODE OVERRIDES ═══ */
        [data-theme="light"] { background: var(--bg-0); color: var(--text); }
        [data-theme="light"] .hero-car-wrap { opacity: 0.3 !important; }
        [data-theme="light"] .hero-car-wrap img, [data-theme="light"] .hero-car-wrap video { filter: brightness(1.2) contrast(0.8) saturate(0.35); }
        [data-theme="light"] .hero-left-fade { background: linear-gradient(to right, #f5f3f0 40%, rgba(245,243,240,0.7) 60%, transparent 80%) !important; }
        [data-theme="light"] .hero-bottom-fade { background: linear-gradient(to top, #f5f3f0, transparent) !important; }
        [data-theme="light"] .hero-top-fade { background: linear-gradient(to bottom, #f5f3f0, transparent) !important; }
        [data-theme="light"] header { backdrop-filter: blur(20px) saturate(1.5); }
        [data-theme="light"] footer { background: #1a1a1a !important; color: #ccc; }
        [data-theme="light"] footer * { color: inherit; }
        [data-theme="light"] footer h4 { color: #E63946 !important; }
        [data-theme="light"] footer p, [data-theme="light"] footer span { color: #aaa !important; }
        [data-theme="light"] footer button { color: #bbb !important; }
        [data-theme="light"] footer button:hover { color: #fff !important; }
        [data-theme="light"] footer a { color: #888 !important; }
        [data-theme="light"] footer a:hover { color: #E63946 !important; }

        [data-theme="light"] ::-webkit-scrollbar-track { background: var(--bg-0); }
        [data-theme="light"] ::-webkit-scrollbar-thumb { background: var(--scroll-thumb); }
        [data-theme="light"] ::selection { background: rgba(214,40,57,0.2); color: #1a1a1a; }
        
        /* Light mode section backgrounds */
        [data-theme="light"] section { transition: background 0.4s ease; }
        
        /* Light mode cards get shadows instead of borders */
        [data-theme="light"] .card-glow::before {
          background: radial-gradient(circle, rgba(214,40,57,0.04) 0%, transparent 70%);
        }
        
        /* Light mode service cards */
        [data-theme="light"] .svc-btn { background: #1a1a1a !important; border-top-color: rgba(230,57,70,0.15) !important; }
        [data-theme="light"] .svc-btn div { color: #ffffff !important; }
        
        /* Light mode booking popup */
        [data-theme="light"] select option { background: #fff; color: #1a1a1a; }
        
        /* Light mode gallery labels — stronger text shadow for visibility */
        [data-theme="light"] .g-label {
          text-shadow: 0 1px 8px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.7) !important;
        }
        
        /* Light mode mobile nav */
        [data-theme="light"] .mob-panel { background: rgba(255,255,255,0.98) !important; backdrop-filter: blur(30px) !important; }
        [data-theme="light"] .mob-panel button { color: var(--nav-link) !important; }
        
        /* Fix text-gradient for light mode */
        [data-theme="light"] .text-gradient {
          background: linear-gradient(90deg, #D62839, #ff4d5a, #D62839);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        /* Theme transition for all elements */
        [data-theme] section, [data-theme] div, [data-theme] header, [data-theme] footer,
        [data-theme] button, [data-theme] input, [data-theme] select, [data-theme] textarea {
          transition: background-color 0.4s ease, color 0.4s ease, border-color 0.4s ease, box-shadow 0.4s ease;
        }

        @media (max-width: 900px) {
          .two-col { grid-template-columns: 1fr !important; }
          .desk-nav { display: none !important; }
          .mob-btn { display: block !important; }
          .mob-theme-toggle { display: block !important; }
          .top-bar { display: none !important; }
          .ig-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .hero-car-wrap { width: 100% !important; opacity: 0.2 !important; }
          .svc-grid { grid-template-columns: 1fr !important; }
        }
        @media (min-width: 901px) { .mob-panel { display: none !important; } }
      `}</style>
      <SEOHead page={page} />
      <SEOContent />
      <Header navigate={navigate} page={page} onBook={() => setBookOpen(true)} theme={theme} setTheme={setTheme} />
      {pages[page] || <HomePage navigate={navigate} onBook={() => setBookOpen(true)} />}
      <Footer navigate={navigate} />
      <WhatsAppFAB />
      <BookingPopup open={bookOpen} onClose={() => setBookOpen(false)} />
    </div>
    </ThemeContext.Provider>
  );
}
